import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
    getTaskStatusLabel,
    getRouteStatusLabel,
    getEvidenceStatusLabel,
} from "../utils/pmoLabels";
import {
    PMO_BADGE_TONES,
    getTaskStatusTone,
    getEvidenceStatusTone,
} from "../utils/pmoStyles";
import { API_BASE } from "../config/orbisConfig";



function formatLogDayLabel(dateValue) {
    if (!dateValue) return "Sin fecha";

    const d = new Date(dateValue);
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);

    const sameDay = (a, b) =>
        a.getFullYear() === b.getFullYear() &&
        a.getMonth() === b.getMonth() &&
        a.getDate() === b.getDate();

    if (sameDay(d, today)) return "Hoy";
    if (sameDay(d, yesterday)) return "Ayer";

    return d.toLocaleDateString("es-MX", {
        year: "numeric",
        month: "long",
        day: "numeric",
    });
}


function groupLogsByDay(logs) {
    const groups = [];

    logs.forEach((log) => {
        const label = formatLogDayLabel(log.created_at);
        const lastGroup = groups[groups.length - 1];

        if (!lastGroup || lastGroup.label !== label) {
            groups.push({
                label,
                items: [log],
            });
        } else {
            lastGroup.items.push(log);
        }
    });

    return groups;
}


function CollapsibleSection({
    title,
    subtitle,
    count = 0,
    storageKey,
    defaultOpen = true,
    children,
}) {
    const [open, setOpen] = useState(() => {
        const saved = localStorage.getItem(storageKey);
        if (saved === null) return defaultOpen;
        return saved === "true";
    });

    useEffect(() => {
        localStorage.setItem(storageKey, String(open));
    }, [open, storageKey]);

    return (
        <div
            style={{
                background: "#fff",
                border: "1px solid #e5e7eb",
                borderRadius: 18,
                padding: 18,
                boxShadow: "0 10px 28px rgba(15, 23, 42, 0.06)",
                marginBottom: 18,
            }}
        >
            <button
                type="button"
                onClick={() => setOpen((prev) => !prev)}
                style={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 12,
                    background: "transparent",
                    border: "none",
                    padding: 0,
                    cursor: "pointer",
                    textAlign: "left",
                }}
            >
                <div>
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 10,
                            flexWrap: "wrap",
                        }}
                    >
                        <div style={{ fontSize: 18, fontWeight: 800, color: "#111827" }}>
                            {title}
                        </div>

                        <span
                            style={{
                                minWidth: 28,
                                height: 28,
                                padding: "0 10px",
                                borderRadius: 999,
                                background: "#f3f4f6",
                                color: "#374151",
                                fontSize: 12,
                                fontWeight: 800,
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            {count}
                        </span>
                    </div>

                    {subtitle ? (
                        <div style={{ marginTop: 4, fontSize: 13, color: "#6b7280" }}>
                            {subtitle}
                        </div>
                    ) : null}
                </div>

                <span
                    style={{
                        width: 32,
                        height: 32,
                        borderRadius: 10,
                        border: "1px solid #e5e7eb",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#374151",
                        background: "#fff",
                        flexShrink: 0,
                    }}
                >
                    {open ? <ChevronUpIcon /> : <ChevronDownIcon />}
                </span>
            </button>

            {open ? (
                <div style={{ marginTop: 16 }}>
                    {children}
                </div>
            ) : null}
        </div>
    );
}

function ChevronDownIcon() {
    return (
        <svg width="16" height="16" viewBox="0 0 20 20" fill="none" aria-hidden="true">
            <path
                d="M5 7.5L10 12.5L15 7.5"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
}

function ChevronUpIcon() {
    return (
        <svg width="16" height="16" viewBox="0 0 20 20" fill="none" aria-hidden="true">
            <path
                d="M5 12.5L10 7.5L15 12.5"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
}
function KpiCard({ label, value, active = false, onClick, tone = "neutral" }) {
    const tones = {
        neutral: {
            bg: "#ffffff",
            border: "#e5e7eb",
            text: "#111827",
            label: "#6b7280",
            accent: "#374151",
        },
        pending: {
            bg: "#f9fafb",
            border: "#d1d5db",
            text: "#111827",
            label: "#6b7280",
            accent: "#4b5563",
        },
        progress: {
            bg: "#eff6ff",
            border: "#bfdbfe",
            text: "#1d4ed8",
            label: "#1d4ed8",
            accent: "#2563eb",
        },
        review: {
            bg: "#fff7ed",
            border: "#fdba74",
            text: "#9a3412",
            label: "#9a3412",
            accent: "#ea580c",
        },
        overdue: {
            bg: "#fff1f2",
            border: "#fecdd3",
            text: "#be123c",
            label: "#be123c",
            accent: "#e11d48",
        },
        done: {
            bg: "#ecfdf5",
            border: "#a7f3d0",
            text: "#047857",
            label: "#047857",
            accent: "#10b981",
        },
        total: {
            bg: "#f8fafc",
            border: "#cbd5e1",
            text: "#0f172a",
            label: "#334155",
            accent: "#0f172a",
        },
    };

    const style = tones[tone] || tones.neutral;

    return (
        <button
            type="button"
            onClick={onClick}
            style={{
                width: "100%",
                textAlign: "left",
                background: active ? style.bg : "#fff",
                border: `1px solid ${active ? style.accent : style.border}`,
                borderRadius: 16,
                padding: 16,
                boxShadow: active
                    ? `0 0 0 3px ${style.border}`
                    : "0 8px 24px rgba(15, 23, 42, 0.04)",
                cursor: "pointer",
                transition: "all 0.2s ease",
            }}
        >
            <div
                style={{
                    fontSize: 12,
                    color: style.label,
                    marginBottom: 8,
                    fontWeight: 700,
                }}
            >
                {label}
            </div>
            <div
                style={{
                    fontSize: 26,
                    fontWeight: 800,
                    color: style.text,
                }}
            >
                {value}
            </div>
        </button>
    );
}

function CommandMetricCard({ label, value, icon, tone = "neutral", active = false, onClick, helper = "" }) {
    const s = PMO_BADGE_TONES[tone] || PMO_BADGE_TONES.neutral;

    return (
        <button
            type="button"
            onClick={onClick}
            title={helper || label}
            style={{
                width: "100%",
                minHeight: 96,
                textAlign: "left",
                borderRadius: 20,
                border: active ? `1px solid ${s.border}` : "1px solid rgba(203,213,225,.82)",
                borderLeft: `6px solid ${s.color}`,
                background: active
                    ? `linear-gradient(135deg, rgba(255,255,255,.96), ${s.bg})`
                    : "rgba(255,255,255,.9)",
                backdropFilter: "blur(10px)",
                boxShadow: active
                    ? `0 0 0 3px ${s.bg}, 0 18px 38px rgba(15,23,42,.09)`
                    : "0 14px 34px rgba(15,23,42,.055)",
                padding: "16px 18px",
                cursor: "pointer",
                transition: "transform .18s ease, box-shadow .18s ease, border-color .18s ease",
            }}
            onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow = active
                    ? `0 0 0 3px ${s.bg}, 0 24px 48px rgba(15,23,42,.12)`
                    : "0 22px 48px rgba(15,23,42,.09)";
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = active
                    ? `0 0 0 3px ${s.bg}, 0 18px 38px rgba(15,23,42,.09)`
                    : "0 14px 34px rgba(15,23,42,.055)";
            }}
        >
            <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                <div style={{ color: s.color, fontSize: 25, fontWeight: 950, width: 38, textAlign: "center", flexShrink: 0 }}>
                    {icon}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ color: "#94a3b8", fontSize: 11, fontWeight: 950, textTransform: "uppercase", letterSpacing: ".08em" }}>
                        {label}
                    </div>
                    <div style={{ marginTop: 6, color: "#0f172a", fontSize: 27, lineHeight: 1, fontWeight: 950, letterSpacing: "-.045em" }}>
                        {value}
                    </div>
                </div>
            </div>
            {helper ? (
                <div style={{ marginTop: 10, color: "#64748b", fontSize: 11, fontWeight: 800, lineHeight: 1.35 }}>
                    {helper}
                </div>
            ) : null}
        </button>
    );
}

function SegmentedRoleControl({ value, onChange, summary }) {
    const options = [
        { value: "todo", label: "Todo", count: summary.total, tone: "neutral" },
        { value: "responsable", label: "Como responsable", count: summary.responsables, tone: "info" },
        { value: "revisor", label: "Como revisor", count: summary.revisores, tone: "purple" },
    ];

    return (
        <div
            style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                padding: 4,
                borderRadius: 999,
                border: "1px solid rgba(148,163,184,.45)",
                background: "rgba(255,255,255,.68)",
                backdropFilter: "blur(12px)",
                boxShadow: "0 12px 28px rgba(15,23,42,.06)",
            }}
            title="Filtro por contexto de rol dentro de Mi Trabajo."
        >
            {options.map((item) => {
                const active = value === item.value;
                const s = PMO_BADGE_TONES[item.tone] || PMO_BADGE_TONES.neutral;
                return (
                    <button
                        key={item.value}
                        type="button"
                        onClick={() => onChange(item.value)}
                        style={{
                            border: active ? `1px solid ${s.color}` : "1px solid transparent",
                            background: active ? "rgba(255,255,255,.92)" : "transparent",
                            color: active ? s.color : "#475569",
                            borderRadius: 999,
                            padding: "8px 12px",
                            fontSize: 12,
                            fontWeight: 950,
                            cursor: "pointer",
                            display: "inline-flex",
                            gap: 8,
                            alignItems: "center",
                            boxShadow: active ? "0 8px 20px rgba(15,23,42,.08)" : "none",
                            whiteSpace: "nowrap",
                        }}
                    >
                        <span>{item.label}</span>
                        <span
                            style={{
                                minWidth: 22,
                                height: 22,
                                padding: "0 7px",
                                borderRadius: 999,
                                background: active ? s.bg : "rgba(248,250,252,.9)",
                                border: `1px solid ${active ? s.border : "#e2e8f0"}`,
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: 11,
                            }}
                        >
                            {item.count}
                        </span>
                    </button>
                );
            })}
        </div>
    );
}


function tooltipText(value) {
    if (value === null || value === undefined) return "";
    if (["string", "number", "boolean"].includes(typeof value)) return String(value);
    if (Array.isArray(value)) return value.map(tooltipText).filter(Boolean).join(" ");
    if (value?.props?.children !== undefined) return tooltipText(value.props.children);
    return "";
}

function normalizeTooltipKey(value) {
    return tooltipText(value)
        .replace(/\s+/g, " ")
        .trim()
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "");
}

function getMyWorkTooltip(value) {
    const raw = tooltipText(value).replace(/\s+/g, " ").trim();
    const key = normalizeTooltipKey(value);

    const map = {
        "todas": "Muestra todas las tareas del contexto seleccionado.",
        "activas": "Muestra tareas abiertas que aún requieren avance, revisión o seguimiento.",
        "criticas": "Muestra tareas con prioridad crítica que siguen abiertas.",
        "pendientes": "Tareas que aún no han iniciado formalmente.",
        "en proceso": "Tareas activas que pueden requerir avance, evidencia o seguimiento.",
        "por revisar": "Avances enviados que esperan dictamen del revisor.",
        "en revision": "Avances enviados que esperan dictamen del revisor.",
        "correcciones recibidas": "Tareas donde el responsable debe corregir o reenviar el avance.",
        "correcciones solicitadas": "Tareas donde tú solicitaste cambios como revisor.",
        "bloqueadas": "Tareas detenidas por dependencia, ruta o condición operativa.",
        "vencidas": "Tareas con fecha compromiso vencida y atención pendiente.",
        "vence hoy": "Tareas con fecha compromiso para la jornada actual.",
        "vence manana": "Tareas con fecha compromiso para el día siguiente.",
        "sin fecha": "Tareas sin fecha compromiso registrada.",
        "terminadas": "Tareas cerradas disponibles para consulta y trazabilidad.",
        "orden inteligente": "Ordena por prioridad operativa: revisión, corrección, vencimiento, prioridad y fecha.",

        "registrar avance": "Abre la tarea para documentar avance, comentario y evidencia como responsable.",
        "revisar avance": "Abre la tarea para validar el avance enviado y emitir dictamen como revisor.",
        "corregir entrega": "El revisor solicitó cambios. Abre la tarea para corregir y reenviar.",
        "atender bloqueo": "La tarea tiene una ruta o dependencia bloqueada. Revisa la condición antes de avanzar.",
        "solo consulta": "La tarea no requiere acción inmediata; puedes abrirla para revisar trazabilidad.",
        "atencion prioritaria": "Tareas con señal urgente por vencimiento, bloqueo, revisión o corrección.",
        "prioridad operativa": "Selección de tareas con mayor urgencia para atender primero.",

        "sin evidencia": "No hay evidencia registrada cuando la tarea puede requerir documentación.",
        "sin alerta": "No se detectan pendientes de evidencia para esta tarea.",
        "falta geo": "La tarea tiene evidencia, pero falta ubicación/geoevidencia requerida.",
        "falta geoevidencia": "Falta evidencia con ubicación para cumplir la validación geográfica.",
        "falta evidencia": "La tarea requiere evidencia antes de continuar.",
        "evidencia ok": "La tarea cuenta con evidencia suficiente para su estado actual.",
        "geo requerida": "Esta tarea requiere evidencia con ubicación automática.",
        "con evidencia": "La tarea ya tiene evidencia registrada.",
        "con evidencia (1)": "La tarea ya tiene una evidencia registrada.",

        "pendiente": "La tarea aún no inicia o espera activación operativa.",
        "borrador": "Sin revisión activa en este momento.",
        "aprobado": "El avance fue aprobado por el revisor.",
        "rechazado": "El avance fue rechazado y requiere atención del responsable.",
        "con cambios": "El revisor solicitó cambios antes de aprobar.",
        "terminada": "La tarea fue cerrada y queda disponible para consulta.",
        "cancelada": "La tarea fue cancelada y no requiere operación activa.",
        "ruta bloqueada": "La tarea no puede avanzar hasta resolver la dependencia o bloqueo operativo.",
        "ruta liberada": "La tarea no presenta bloqueo operativo activo.",
        "inicio de ruta": "La tarea no depende de otra tarea previa.",
        "vencida": "La fecha compromiso ya venció y requiere atención.",
        "paquete wbs": "Esta tarea agrupa tareas hijas dentro de la estructura WBS.",

        "critica": "Prioridad crítica: requiere atención ejecutiva u operativa inmediata.",
        "urgente": "Tarea crítica que debe atenderse con prioridad alta.",
        "prioridad alta": "Tarea con prioridad alta dentro del plan operativo.",
        "alta": "Prioridad alta dentro del plan operativo.",
        "media": "Prioridad media dentro del plan operativo.",
        "baja": "Prioridad baja dentro del plan operativo.",

        "portafolio pmo": "Muestra la carga de trabajo de todos los proyectos disponibles para tu usuario.",
        "proyecto completo": "Muestra la carga de trabajo del proyecto seleccionado.",
    };

    if (map[key]) return map[key];
    if (key.startsWith("filtro activo:")) return `Filtro activo del tablero: ${raw.replace(/^Filtro activo:\s*/i, "")}.`;
    if (key.startsWith("con evidencia")) return "La tarea ya tiene evidencia registrada.";

    return raw ? `Estado del tablero: ${raw}.` : "Elemento del tablero operativo.";
}

function StatusBadge({ children, bg = "#eef2ff", color = "#3730a3", tone = "", title = "" }) {
    const toneStyle = tone ? PMO_BADGE_TONES[tone] : null;
    const finalBg = toneStyle?.bg || bg;
    const finalColor = toneStyle?.color || color;
    const finalBorder = toneStyle?.border || "transparent";
    const tooltip = title || getMyWorkTooltip(children);

    return (
        <span
            title={tooltip}
            style={{
                display: "inline-flex",
                alignItems: "center",
                padding: "4px 10px",
                borderRadius: 999,
                fontSize: 12,
                fontWeight: 800,
                background: finalBg,
                color: finalColor,
                border: `1px solid ${finalBorder}`,
                whiteSpace: "nowrap",
                cursor: tooltip ? "help" : "default",
            }}
        >
            {children}
        </span>
    );
}

function getOwnerDisplay(task = {}) {
    return (
        task.owner_name ||
        task.responsible_name ||
        task.assigned_to_name ||
        task.owner_user_name ||
        task.owner_email ||
        task.responsible_email ||
        "Sin responsable"
    );
}

function getReviewerDisplay(task = {}) {
    return (
        task.reviewer_name ||
        task.reviewer_user_name ||
        task.review_owner_name ||
        task.reviewer_email ||
        "Sin revisor"
    );
}

function isOwnerAssignment(task = {}) {
    const role = String(task.work_role || "").toLowerCase();
    return (
        role === "responsable" ||
        role === "responsable_y_revisor" ||
        Number(task.is_owner_assignment || 0) === 1
    );
}

function isReviewerAssignment(task = {}) {
    const role = String(task.work_role || "").toLowerCase();
    return (
        role === "revisor" ||
        role === "responsable_y_revisor" ||
        Number(task.is_review_assignment || 0) === 1
    );
}

function getSuggestedAction(task = {}, tasks = []) {
    const mode = getTaskOpenMode(task);
    const status = String(task.status || "").toLowerCase();

    if (isTaskBlocked(task, tasks)) return { label: "Atender bloqueo", tone: "danger" };
    if (isTaskCorrectionForOwner(task)) return { label: "Corregir entrega", tone: "warning" };
    if (mode === "review") return { label: "Revisar avance", tone: "purple" };
    if (mode === "progress") return { label: "Registrar avance", tone: "info" };
    if (status === "terminada") return { label: "Solo consulta", tone: "success" };
    return { label: "Solo consulta", tone: "neutral" };
}

function getAttentionReasons(task = {}, tasks = []) {
    const reasons = [];
    if (isOverdue(task.due_date, task.status)) reasons.push("Vencida");
    if (isTaskBlocked(task, tasks)) reasons.push("Ruta bloqueada");
    if (isTaskInReview(task)) reasons.push("En revisión");
    if (isTaskCorrectionForOwner(task)) reasons.push("Correccion recibida");
    if (isTaskCorrectionRequestedByMe(task)) reasons.push("Correccion solicitada");
    if (["critica", "alta"].includes(String(task.priority || "").toLowerCase())) {
        reasons.push(getPriorityStyle(task.priority).label);
    }
    const evidence = getEvidenceDisplay(task);
    if (["Sin evidencia", "Falta geo", "Falta geoevidencia", "Falta evidencia"].includes(evidence)) {
        reasons.push(evidence);
    }
    return reasons.slice(0, 4);
}

function getEvidenceDisplay(task = {}) {
    const meta = getTaskEvidenceMeta(task);
    if (meta.badges?.length) return meta.badges[0].label;
    if (!shouldShowEvidenceWarnings(task)) return "Sin alerta";
    return getEvidenceStatusLabel(task);
}

function getEvidenceToneForTask(task = {}) {
    const label = getEvidenceDisplay(task).toLowerCase();
    if (label.includes("ok") || label.includes("completa") || label.includes("sin alerta")) return "success";
    if (label.includes("geo") || label.includes("pendiente")) return "warning";
    if (label.includes("sin evidencia") || label.includes("falta")) return "danger";
    return getEvidenceStatusTone(task);
}


function getSuggestedActionDetail(task = {}, tasks = []) {
    if (isTaskBlocked(task, tasks)) {
        return "La ruta operativa está bloqueada. Revisa dependencia, fecha o condición antes de continuar.";
    }

    if (isTaskCorrectionForOwner(task)) {
        return task.last_review_comment || "El revisor solicitó cambios. Corrige la entrega y reenvía el avance.";
    }

    if (isTaskInReview(task) && isReviewerAssignment(task)) {
        return "Hay un avance esperando tu dictamen. Revisa evidencia, comentario y ubicación antes de decidir.";
    }

    if (isTaskCorrectionRequestedByMe(task)) {
        return "Solicitaste cambios. Espera el reenvío del responsable o consulta la trazabilidad.";
    }

    if (isOverdue(task.due_date, task.status)) {
        return "La fecha compromiso ya venció. Atiende el avance o registra seguimiento.";
    }

    if (isToday(task.due_date) && task.status !== "terminada") {
        return "La tarea vence hoy. Conviene actualizar avance o cerrar seguimiento.";
    }

    if (String(task.status || "").toLowerCase() === "terminada") {
        return "La tarea está cerrada. Disponible para consulta y trazabilidad.";
    }

    return "Abre la ficha para registrar avance, revisar contexto o consultar la trazabilidad.";
}

function getWorkRoleLabel(task = {}) {
    if (isOwnerAssignment(task) && isReviewerAssignment(task)) return "Responsable y revisor";
    if (isReviewerAssignment(task)) return "Revisor";
    if (isOwnerAssignment(task)) return "Responsable";
    return "Consulta";
}

function getWorkRoleTone(task = {}) {
    if (isOwnerAssignment(task) && isReviewerAssignment(task)) return "purple";
    if (isReviewerAssignment(task)) return "purple";
    if (isOwnerAssignment(task)) return "info";
    return "neutral";
}

function getDueTone(task = {}) {
    if (String(task.status || "").toLowerCase() === "terminada") return "success";
    if (isOverdue(task.due_date, task.status)) return "danger";
    if (isToday(task.due_date) && task.status !== "terminada") return "warning";
    if (isTomorrow(task.due_date) && task.status !== "terminada") return "info";
    return "neutral";
}

function MiniInfoTile({ label, value, tone = "neutral", title = "" }) {
    const s = PMO_BADGE_TONES[tone] || PMO_BADGE_TONES.neutral;

    return (
        <div
            title={title || value || label}
            style={{
                border: `1px solid ${s.border || "#e5e7eb"}`,
                borderRadius: 14,
                padding: 11,
                background: "#ffffff",
                minWidth: 0,
            }}
        >
            <div style={{ color: "#64748b", fontSize: 10, fontWeight: 950, textTransform: "uppercase", letterSpacing: ".04em" }}>
                {label}
            </div>
            <div style={{ marginTop: 5, color: s.color || "#0f172a", fontSize: 13, fontWeight: 950, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {value || "—"}
            </div>
        </div>
    );
}

function InsightBanner({ insight }) {
    const s = PMO_BADGE_TONES[insight.tone] || PMO_BADGE_TONES.info;

    return (
        <div
            style={{
                borderRadius: 28,
                background: "linear-gradient(135deg, rgba(255,255,255,.98) 0%, rgba(248,250,252,.96) 100%)",
                border: `1px solid ${s.border}`,
                borderLeft: `5px solid ${s.color}`,
                boxShadow: "0 16px 42px rgba(15,23,42,.055)",
                padding: 20,
                display: "flex",
                justifyContent: "space-between",
                gap: 18,
                alignItems: "center",
                flexWrap: "wrap",
                marginBottom: 18,
            }}
        >
            <div style={{ minWidth: 0 }}>
                <div
                    style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 8,
                        padding: "7px 11px",
                        borderRadius: 999,
                        background: "#ffffff",
                        border: `1px solid ${s.border}`,
                        color: s.color,
                        fontSize: 11,
                        fontWeight: 950,
                        letterSpacing: ".06em",
                        textTransform: "uppercase",
                        marginBottom: 12,
                    }}
                >
                    Workflow personal PMO
                </div>
                <h2 style={{ margin: 0, color: "#0F172A", fontSize: 32, lineHeight: 1.05, fontWeight: 950, letterSpacing: "-.05em" }}>
                    Mi trabajo
                </h2>
                <div style={{ marginTop: 10, color: s.color, fontSize: 16, fontWeight: 950 }}>
                    {insight.title}
                </div>
                <p style={{ margin: "6px 0 0", color: "#64748B", fontSize: 14, lineHeight: 1.5, maxWidth: 860, fontWeight: 750 }}>
                    {insight.message}
                </p>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(120px, 1fr))", gap: 10, minWidth: 280 }}>
                <MiniExecutiveMetric label="Activas" value={insight.activeTasks} tone="info" title="Tareas abiertas que requieren avance, revisión o seguimiento." />
                <MiniExecutiveMetric label="Atención" value={insight.attentionTasks} tone={insight.tone} title="Tareas vencidas, bloqueadas, en revisión o con corrección requerida." />
            </div>
        </div>
    );
}

function MiniExecutiveMetric({ label, value, tone = "neutral", title = "" }) {
    const s = PMO_BADGE_TONES[tone] || PMO_BADGE_TONES.neutral;
    return (
        <div title={title || label} style={{ borderRadius: 20, background: "rgba(255,255,255,.9)", border: `1px solid ${s.border}`, padding: 14, boxShadow: "0 10px 22px rgba(15,23,42,.045)", cursor: title ? "help" : "default" }}>
            <div style={{ color: "#64748B", fontSize: 10, fontWeight: 950, textTransform: "uppercase" }}>{label}</div>
            <div style={{ marginTop: 4, color: s.color, fontSize: 26, fontWeight: 950, letterSpacing: "-.04em" }}>{value}</div>
        </div>
    );
}

function WorkChartCard({ title, subtitle, children }) {
    return (
        <div style={{ borderRadius: 24, background: "#FFFFFF", border: "1px solid #E2E8F0", boxShadow: "0 18px 45px rgba(15,23,42,.06)", padding: 18, minHeight: 250 }}>
            <div style={{ marginBottom: 16 }}>
                <div style={{ color: "#0F172A", fontSize: 15, fontWeight: 950 }}>{title}</div>
                {subtitle ? <div style={{ marginTop: 4, color: "#64748B", fontSize: 12, fontWeight: 750, lineHeight: 1.35 }}>{subtitle}</div> : null}
            </div>
            {children}
        </div>
    );
}

function FlowBarChart({ items = [] }) {
    const total = Math.max(1, items.reduce((acc, item) => acc + Number(item.value || 0), 0));
    return (
        <div style={{ display: "grid", gap: 12 }}>
            {items.map((item) => {
                const width = Math.max(3, (Number(item.value || 0) / total) * 100);
                const s = PMO_BADGE_TONES[item.tone] || PMO_BADGE_TONES.neutral;
                return (
                    <div key={item.label}>
                        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, marginBottom: 6 }}>
                            <span style={{ color: "#334155", fontSize: 12, fontWeight: 900 }}>{item.label}</span>
                            <span style={{ color: s.color, fontSize: 12, fontWeight: 950 }}>{item.value}</span>
                        </div>
                        <div style={{ height: 10, borderRadius: 999, background: "#E2E8F0", overflow: "hidden" }}>
                            <div style={{ width: `${width}%`, height: "100%", borderRadius: 999, background: s.color }} />
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

function DonutProductivity({ completed = 0, total = 0 }) {
    const pct = total > 0 ? Math.round((completed / total) * 100) : 0;
    return (
        <div style={{ display: "flex", alignItems: "center", gap: 18, justifyContent: "center", minHeight: 170 }}>
            <div style={{ width: 132, height: 132, borderRadius: "50%", background: `conic-gradient(#16A34A ${pct * 3.6}deg, #E2E8F0 0deg)`, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 18px 36px rgba(22,163,74,.14)" }}>
                <div style={{ width: 86, height: 86, borderRadius: "50%", background: "#FFFFFF", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column" }}>
                    <div style={{ color: "#047857", fontSize: 26, fontWeight: 950, letterSpacing: "-.05em" }}>{pct}%</div>
                    <div style={{ color: "#64748B", fontSize: 10, fontWeight: 900 }}>CIERRE</div>
                </div>
            </div>
            <div style={{ minWidth: 120 }}>
                <div style={{ color: "#0F172A", fontSize: 22, fontWeight: 950 }}>{completed}/{total}</div>
                <div style={{ marginTop: 4, color: "#64748B", fontSize: 12, fontWeight: 800, lineHeight: 1.35 }}>tareas terminadas sobre la carga total visible.</div>
            </div>
        </div>
    );
}

function ProjectLoadChart({ items = [] }) {
    const max = Math.max(1, ...items.map((x) => Number(x.total || 0)));
    if (!items.length) return <EmptyMini text="No hay proyectos con tareas para mostrar." />;
    return (
        <div style={{ display: "grid", gap: 12 }}>
            {items.slice(0, 5).map((item) => {
                const width = Math.max(5, (Number(item.total || 0) / max) * 100);
                const tone = item.overdue > 0 ? "danger" : item.review > 0 ? "purple" : "info";
                const s = PMO_BADGE_TONES[tone] || PMO_BADGE_TONES.info;
                return (
                    <div key={item.projectId || item.projectName}>
                        <div style={{ display: "flex", justifyContent: "space-between", gap: 10, marginBottom: 6 }}>
                            <span style={{ color: "#334155", fontSize: 12, fontWeight: 900, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.projectName}</span>
                            <span style={{ color: s.color, fontSize: 12, fontWeight: 950 }}>{item.total}</span>
                        </div>
                        <div style={{ height: 10, borderRadius: 999, background: "#E2E8F0", overflow: "hidden" }}>
                            <div style={{ width: `${width}%`, height: "100%", borderRadius: 999, background: s.color }} />
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

function MiniFlowLine({ label, value, tone = "neutral", max = 1 }) {
    const s = PMO_BADGE_TONES[tone] || PMO_BADGE_TONES.neutral;
    const width = Math.max(Number(value || 0) > 0 ? 5 : 2, (Number(value || 0) / Math.max(1, max)) * 100);

    return (
        <div>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 10, marginBottom: 5 }}>
                <span style={{ color: "#334155", fontSize: 11, fontWeight: 900 }}>{label}</span>
                <span style={{ color: s.color, fontSize: 11, fontWeight: 950 }}>{value}</span>
            </div>
            <div style={{ height: 8, borderRadius: 999, background: "#E2E8F0", overflow: "hidden" }}>
                <div style={{ width: `${width}%`, height: "100%", borderRadius: 999, background: s.color }} />
            </div>
        </div>
    );
}

function OperationalSidePanel({ summary, flowData = [], agendaData = [], projectLoadData = [], productivityRate = 0 }) {
    const maxFlow = Math.max(1, ...flowData.map((item) => Number(item.value || 0)));
    const maxAgenda = Math.max(1, ...agendaData.map((item) => Number(item.value || 0)));
    const topProject = projectLoadData[0] || null;

    return (
        <aside
            style={{
                borderRadius: 24,
                background: "rgba(255,255,255,.88)",
                border: "1px solid rgba(203,213,225,.82)",
                boxShadow: "0 20px 48px rgba(15,23,42,.07)",
                backdropFilter: "blur(12px)",
                padding: 16,
                display: "grid",
                gap: 16,
                alignSelf: "start",
            }}
        >
            <div>
                <div style={{ color: "#64748B", fontSize: 10, fontWeight: 950, textTransform: "uppercase", letterSpacing: ".075em" }}>
                    Análisis operativo
                </div>
                <div style={{ marginTop: 4, color: "#0F172A", fontSize: 18, fontWeight: 950, letterSpacing: "-.03em" }}>
                    Lectura rápida PMO
                </div>
                <div style={{ marginTop: 4, color: "#64748B", fontSize: 12, fontWeight: 750, lineHeight: 1.35 }}>
                    Contexto de apoyo para leer carga, flujo y presión sin desplazar la operación diaria.
                </div>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 14, padding: 14, borderRadius: 18, border: "1px solid #E2E8F0", background: "#FFFFFF" }}>
                <div
                    style={{
                        width: 76,
                        height: 76,
                        borderRadius: "50%",
                        background: `conic-gradient(#059669 ${productivityRate * 3.6}deg, #E2E8F0 0deg)`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        boxShadow: "0 14px 28px rgba(5,150,105,.13)",
                        flexShrink: 0,
                    }}
                >
                    <div style={{ width: 52, height: 52, borderRadius: "50%", background: "#FFFFFF", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column" }}>
                        <div style={{ color: "#047857", fontSize: 18, fontWeight: 950 }}>{productivityRate}%</div>
                        <div style={{ color: "#64748B", fontSize: 8, fontWeight: 950 }}>CIERRE</div>
                    </div>
                </div>
                <div>
                    <div style={{ color: "#0F172A", fontSize: 20, fontWeight: 950 }}>{summary.terminadas}/{summary.total}</div>
                    <div style={{ color: "#64748B", fontSize: 11, fontWeight: 800, lineHeight: 1.35 }}>tareas cerradas sobre la carga visible.</div>
                    {summary.vencidas > 0 ? (
                        <div style={{ marginTop: 6, color: "#b91c1c", fontSize: 11, fontWeight: 950 }}>
                            {summary.vencidas} tarea(s) requieren atención inmediata.
                        </div>
                    ) : null}
                </div>
            </div>

            <div style={{ display: "grid", gap: 10 }}>
                <div style={{ color: "#0F172A", fontSize: 13, fontWeight: 950 }}>Estado del flujo</div>
                {flowData.map((item) => (
                    <MiniFlowLine key={item.label} label={item.label} value={item.value} tone={item.tone} max={maxFlow} />
                ))}
            </div>

            <div style={{ display: "grid", gap: 10 }}>
                <div style={{ color: "#0F172A", fontSize: 13, fontWeight: 950 }}>Presión de agenda</div>
                {agendaData.map((item) => (
                    <MiniFlowLine key={item.label} label={item.label} value={item.value} tone={item.tone} max={maxAgenda} />
                ))}
            </div>

            <div style={{ display: "grid", gap: 10 }}>
                <div style={{ color: "#0F172A", fontSize: 13, fontWeight: 950 }}>Carga por proyecto</div>
                {topProject ? (
                    <MiniFlowLine
                        label={topProject.projectName}
                        value={topProject.total}
                        tone={topProject.overdue > 0 ? "danger" : topProject.review > 0 ? "purple" : "info"}
                        max={Math.max(1, ...projectLoadData.map((item) => Number(item.total || 0)))}
                    />
                ) : (
                    <div style={{ color: "#64748B", fontSize: 12, fontWeight: 800 }}>Sin carga visible.</div>
                )}
            </div>
        </aside>
    );
}

function PriorityNowPanel({ items = [], allTasks = [], onOpen }) {
    return (
        <div
            style={{
                borderRadius: 24,
                background: "linear-gradient(135deg, rgba(255,255,255,.96) 0%, rgba(248,250,252,.92) 100%)",
                border: "1px solid #E2E8F0",
                boxShadow: "0 18px 45px rgba(15,23,42,.06)",
                padding: 18,
                marginBottom: 18,
            }}
        >
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start", marginBottom: 14, flexWrap: "wrap" }}>
                <div>
                    <div style={{ color: "#0F172A", fontSize: 17, fontWeight: 950 }}>
                        Atención inmediata
                    </div>
                    <div style={{ marginTop: 4, color: "#64748B", fontSize: 12, fontWeight: 750, lineHeight: 1.45 }}>
                        Spotlight de tareas que requieren acción prioritaria. También aparecen en el tablero principal para consulta y seguimiento.
                    </div>
                </div>
                <StatusBadge tone="purple">Atención prioritaria</StatusBadge>
            </div>
            {items.length ? (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 12 }}>
                    {items.slice(0, 6).map((task) => {
                        const overdue = isOverdue(task.due_date, task.status);
                        const blocked = isTaskBlocked(task, allTasks);
                        const suggested = getSuggestedAction(task, allTasks);
                        const owner = getOwnerDisplay(task);
                        const reviewer = getReviewerDisplay(task);
                        const roleLabel = isOwnerAssignment(task) && isReviewerAssignment(task)
                            ? "Responsable y revisor"
                            : isReviewerAssignment(task)
                                ? "Revisor"
                                : isOwnerAssignment(task)
                                    ? "Responsable"
                                    : "Consulta";
                        const badge = overdue
                            ? "Vencida"
                            : blocked
                                ? "Ruta bloqueada"
                                : isTaskInReview(task)
                                    ? "En revisión"
                                    : isTaskCorrection(task)
                                        ? "Requiere ajustes"
                                        : getTaskStatusLabel(task.status);
                        const tone = overdue || blocked
                            ? "danger"
                            : isTaskInReview(task)
                                ? "purple"
                                : isTaskCorrection(task)
                                    ? "warning"
                                    : getTaskStatusTone(task.status);

                        return (
                            <button
                                key={task.id}
                                type="button"
                                onClick={() => onOpen?.(task)}
                                title="Abrir ficha operativa de la tarea"
                                style={{
                                    textAlign: "left",
                                    border: "1px solid #E2E8F0",
                                    background: "#FFFFFF",
                                    borderRadius: 18,
                                    padding: 14,
                                    cursor: "pointer",
                                    boxShadow: "0 10px 24px rgba(15,23,42,.045)",
                                    transition: "transform .15s ease, box-shadow .15s ease, border-color .15s ease",
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.transform = "translateY(-1px)";
                                    e.currentTarget.style.boxShadow = "0 16px 34px rgba(15,23,42,.08)";
                                    e.currentTarget.style.borderColor = "#BFDBFE";
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = "translateY(0)";
                                    e.currentTarget.style.boxShadow = "0 10px 24px rgba(15,23,42,.045)";
                                    e.currentTarget.style.borderColor = "#E2E8F0";
                                }}
                            >
                                <div style={{ display: "flex", justifyContent: "space-between", gap: 8, marginBottom: 8, alignItems: "center" }}>
                                    <div style={{ color: "#2563EB", fontSize: 11, fontWeight: 950 }}>{task.wbs_code || "SIN WBS"}</div>
                                    <StatusBadge tone={tone}>{badge}</StatusBadge>
                                </div>
                                <div style={{ color: "#0F172A", fontSize: 14, fontWeight: 950, lineHeight: 1.25 }}>
                                    {task.name || "Sin nombre"}
                                </div>
                                <div style={{ marginTop: 6, color: "#64748B", fontSize: 11, fontWeight: 800, lineHeight: 1.4 }}>
                                    {task.project_name || "Sin proyecto"}
                                </div>
                                <div style={{ marginTop: 10, display: "grid", gap: 5 }}>
                                    <div style={{ color: "#334155", fontSize: 11, fontWeight: 900 }}>
                                        Tu rol: <span style={{ color: "#0F172A" }}>{roleLabel}</span>
                                    </div>
                                    <div style={{ color: "#334155", fontSize: 11, fontWeight: 900 }}>
                                        Responsable: <span style={{ color: "#0F172A" }}>{owner}</span>
                                    </div>
                                    <div style={{ color: "#334155", fontSize: 11, fontWeight: 900 }}>
                                        Revisor: <span style={{ color: "#0F172A" }}>{reviewer}</span>
                                    </div>
                                </div>
                                <div style={{ marginTop: 10, display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
                                    {getAttentionReasons(task, allTasks).map((reason) => (
                                        <StatusBadge key={reason} tone={reason.includes("bloqueada") || reason.includes("Vencida") ? "danger" : reason.includes("revision") ? "purple" : "warning"}>{reason}</StatusBadge>
                                    ))}
                                    <StatusBadge tone={suggested.tone}>{suggested.label}</StatusBadge>
                                    <span
                                        style={{
                                            marginLeft: "auto",
                                            display: "inline-flex",
                                            alignItems: "center",
                                            padding: "5px 10px",
                                            borderRadius: 999,
                                            border: "1px solid #BFDBFE",
                                            background: "#EFF6FF",
                                            color: "#1D4ED8",
                                            fontSize: 11,
                                            fontWeight: 950,
                                        }}
                                    >
                                        Abrir tarea
                                    </span>
                                </div>
                            </button>
                        );
                    })}
                </div>
            ) : (
                <EmptyMini text="No hay tareas en atención inmediata. El tablero no detecta vencimientos, bloqueos, revisiones o correcciones urgentes." />
            )}
        </div>
    );
}

function ActionButton({
    children,
    onClick,
    type = "button",
    bg = "#111827",
    color = "#fff",
    border = "none",
    disabled = false,
}) {
    return (
        <button
            type={type}
            onClick={onClick}
            disabled={disabled}
            style={{
                padding: "10px 14px",
                borderRadius: 10,
                border,
                background: disabled ? "#9ca3af" : bg,
                color,
                cursor: disabled ? "not-allowed" : "pointer",
                fontWeight: 700,
                fontSize: 13,
            }}
        >
            {children}
        </button>
    );
}

function FilterButton({ children, active, onClick, count = null, tone = "neutral", title = "" }) {
    const s = PMO_BADGE_TONES[tone] || PMO_BADGE_TONES.neutral;

    return (
        <button
            type="button"
            title={title || getMyWorkTooltip(children)}
            onClick={onClick}
            style={{
                padding: "9px 12px",
                borderRadius: 999,
                border: active ? `1px solid ${s.color}` : `1px solid ${s.border}`,
                background: active ? s.bg : "#fff",
                color: active ? s.color : "#334155",
                fontWeight: 900,
                cursor: "pointer",
                fontSize: 12,
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                boxShadow: active ? `0 0 0 3px ${s.bg}` : "0 6px 14px rgba(15,23,42,.04)",
                whiteSpace: "nowrap",
            }}
        >
            <span>{children}</span>
            {count !== null ? (
                <span
                    style={{
                        minWidth: 22,
                        height: 22,
                        padding: "0 7px",
                        borderRadius: 999,
                        background: active ? "#FFFFFF" : s.bg,
                        color: s.color,
                        border: `1px solid ${s.border}`,
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 11,
                        fontWeight: 950,
                    }}
                >
                    {count}
                </span>
            ) : null}
        </button>
    );
}


function InfoBox({ label, value, title = "" }) {
    return (
        <div
            title={title}
            style={{
                border: "1px solid #e5e7eb",
                borderRadius: 12,
                padding: 12,
                background: "#fafafa",
                cursor: title ? "help" : "default",
            }}
        >
            <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 6 }}>
                {label}
            </div>
            <div style={{ fontSize: 14, fontWeight: 700, color: "#111827" }}>
                {value}
            </div>
        </div>
    );
}

function SectionCard({ title, subtitle, children }) {
    return (
        <div
            style={{
                background: "#fff",
                border: "1px solid #e5e7eb",
                borderRadius: 18,
                padding: 18,
                boxShadow: "0 10px 28px rgba(15, 23, 42, 0.06)",
                marginBottom: 18,
            }}
        >
            <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 18, fontWeight: 800, color: "#111827" }}>
                    {title}
                </div>
                {subtitle ? (
                    <div style={{ marginTop: 4, fontSize: 13, color: "#6b7280" }}>
                        {subtitle}
                    </div>
                ) : null}
            </div>

            {children}
        </div>
    );
}

function EmptyMini({ text }) {
    return (
        <div
            style={{
                border: "1px dashed #CBD5E1",
                borderRadius: 18,
                padding: 22,
                color: "#64748B",
                background: "linear-gradient(135deg, #FFFFFF 0%, #F8FAFC 100%)",
                fontSize: 13,
                fontWeight: 800,
                display: "flex",
                alignItems: "center",
                gap: 12,
            }}
        >
            <span
                aria-hidden="true"
                style={{
                    width: 30,
                    height: 30,
                    borderRadius: 12,
                    border: "1px solid #E2E8F0",
                    background: "#FFFFFF",
                    color: "#2563EB",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 950,
                    flexShrink: 0,
                }}
            >
                i
            </span>
            <span>{text}</span>
        </div>
    );
}

function getStatusStyle(status) {
    switch (status) {
        case "pendiente":
            return { bg: "#f3f4f6", color: "#374151", label: "Pendiente" };
        case "en_proceso":
            return { bg: "#dbeafe", color: "#1d4ed8", label: "En proceso" };
        case "en_revision":
            return { bg: "#fef3c7", color: "#92400e", label: "En revisión" };
        case "bloqueada":
            return { bg: "#ffffff", color: "#b91c1c", label: "Bloqueada" };
        case "terminada":
            return { bg: "#dcfce7", color: "#166534", label: "Terminada" };
        case "cancelada":
            return { bg: "#f3f4f6", color: "#6b7280", label: "Cancelada" };
        default:
            return { bg: "#f3f4f6", color: "#374151", label: status || "Sin estatus" };
    }
}

function getReviewStyle(reviewStatus) {
    switch ((reviewStatus || "").toLowerCase()) {
        case "en_revision":
        case "pendiente_revision":
            return { bg: "#fef3c7", color: "#92400e", label: "En revisión" };
        case "aprobado":
            return { bg: "#dcfce7", color: "#166534", label: "Aprobado" };
        case "rechazado":
            return { bg: "#ffffff", color: "#b91c1c", label: "Rechazado" };
        case "con_cambios":
            return { bg: "#ffffff", color: "#9a3412", label: "Con cambios" };
        case "borrador":
            return { bg: "#f3f4f6", color: "#374151", label: "Borrador" };
        default:
            return { bg: "#f3f4f6", color: "#374151", label: reviewStatus || "—" };
    }
}

function isTaskInReview(task) {
    return ["en_revision", "pendiente_revision"].includes(
        String(task?.review_status || "").toLowerCase()
    );
}

function isTaskCorrection(task) {
    return ["rechazado", "con_cambios"].includes(
        String(task?.review_status || "").toLowerCase()
    );
}

function isTaskCorrectionForOwner(task) {
    return (
        String(task?.work_role || "").toLowerCase() === "responsable" &&
        ["rechazado", "con_cambios"].includes(
            String(task?.review_status || "").toLowerCase()
        )
    );
}

function isTaskCorrectionRequestedByMe(task) {
    return (
        String(task?.work_role || "").toLowerCase() === "revisor" &&
        ["rechazado", "con_cambios"].includes(
            String(task?.review_status || "").toLowerCase()
        )
    );
}

function getPriorityRank(priority) {
    switch ((priority || "").toLowerCase()) {
        case "critica":
            return 1;
        case "alta":
            return 2;
        case "media":
            return 3;
        case "baja":
            return 4;
        default:
            return 5;
    }
}

function getPriorityStyle(priority) {
    switch ((priority || "").toLowerCase()) {
        case "critica":
            return { bg: "#ffffff", color: "#b91c1c", label: "Crítica" };
        case "alta":
            return { bg: "#ffedd5", color: "#c2410c", label: "Alta" };
        case "media":
            return { bg: "#fef3c7", color: "#92400e", label: "Media" };
        case "baja":
            return { bg: "#dcfce7", color: "#166534", label: "Baja" };
        default:
            return { bg: "#f3f4f6", color: "#374151", label: priority || "—" };
    }
}


function getChildTasks(taskId, tasks) {
    return Array.isArray(tasks)
        ? tasks.filter((t) => String(t.parent_task_id || "") === String(taskId || ""))
        : [];
}

function hasChildTasks(taskId, tasks) {
    return getChildTasks(taskId, tasks).length > 0;
}

function getDependencyTask(task, tasks) {
    if (!task?.depends_on_task_id) return null;
    return (tasks || []).find(
        (t) => String(t.id || "") === String(task.depends_on_task_id || "")
    ) || null;
}

function isDependencyStarted(dependency) {
    if (!dependency) return true;

    const status = String(dependency.status || "").toLowerCase();
    const progress = Number(dependency.progress_percent || 0);

    return progress > 0 || ["en_proceso", "terminada"].includes(status);
}

function isDependencyFinished(dependency) {
    if (!dependency) return true;

    const requiresReview = Number(dependency.requires_review || 0) === 1;
    const status = String(dependency.status || "").toLowerCase();
    const reviewStatus = String(dependency.review_status || "").toLowerCase();
    const progress = Number(dependency.progress_percent || 0);

    if (requiresReview) {
        return progress >= 100 && reviewStatus === "aprobado";
    }

    return status === "terminada" || progress >= 100;
}

function isDependencyReleased(task, tasks) {
    if (!task?.depends_on_task_id) return true;

    if (task?.dependency_is_blocked !== undefined && task?.dependency_is_blocked !== null) {
        return Number(task.dependency_is_blocked || 0) !== 1;
    }

    const dependencyFromPayload = task?.dependency_status?.dependency_task || null;
    const dependency = dependencyFromPayload || getDependencyTask(task, tasks);

    if (!dependency) return true;

    const dependencyType = String(
        task?.dependency_status?.dependency_type || task?.dependency_type || "FS"
    ).toUpperCase();

    switch (dependencyType) {
        case "SS":
        case "SF":
            return isDependencyStarted(dependency);

        case "FF":
            // FF no bloquea el inicio; la validación fuerte debe aplicarse al cierre.
            return true;

        case "FS":
        default:
            return isDependencyFinished(dependency);
    }
}

function isTaskBlocked(task, tasks) {
    if (Number(task?.is_blocked || 0) === 1 && !task?.depends_on_task_id) return true;

    if (task?.depends_on_task_id && !isDependencyReleased(task, tasks)) {
        return true;
    }

    return Number(task?.is_blocked || 0) === 1 && Number(task?.dependency_is_blocked || 0) !== 0;
}

function getRouteStyle(task, tasks) {
    if (isTaskBlocked(task, tasks)) {
        return { bg: "#ffffff", color: "#b91c1c", label: "Ruta bloqueada" };
    }

    if (task?.depends_on_task_id) {
        return { bg: "#ecfeff", color: "#0f766e", label: "Ruta liberada" };
    }

    return { bg: "#f3f4f6", color: "#374151", label: "Inicio de ruta" };
}


function getTaskEvidenceMeta(task) {
    const evidenceRequired = Number(task?.evidence_required || 0) === 1;
    const geoEvidenceRequired = Number(task?.geo_evidence_required || 0) === 1;
    const total = Number(task?.evidence_total || 0);
    const withGeo = Number(task?.evidence_with_geo || 0);

    const badges = [];

    if (!shouldShowEvidenceWarnings(task)) {
        return {
            evidenceRequired,
            geoEvidenceRequired,
            total,
            withGeo,
            badges,
        };
    }

    if (evidenceRequired && total <= 0) {
        badges.push({ bg: "#ffffff", color: "#b91c1c", label: "Sin evidencia" });
    } else if (geoEvidenceRequired && withGeo <= 0) {
        badges.push({ bg: "#ffffff", color: "#9a3412", label: "Falta geo" });
    } else if (
        (evidenceRequired && total > 0 && !geoEvidenceRequired) ||
        (geoEvidenceRequired && withGeo > 0)
    ) {
        badges.push({ bg: "#ecfdf5", color: "#166534", label: "Evidencia OK" });
    } else if (total > 0) {
        badges.push({ bg: "#eff6ff", color: "#1d4ed8", label: `Con evidencia (${total})` });
    }

    if (geoEvidenceRequired && withGeo <= 0) {
        badges.push({ bg: "#ecfeff", color: "#0f766e", label: "Geo requerida" });
    }

    return {
        evidenceRequired,
        geoEvidenceRequired,
        total,
        withGeo,
        badges,
    };
}
function formatDate(dateValue) {
    if (!dateValue) return "Sin fecha";
    try {
        const date = new Date(dateValue);
        return date.toLocaleDateString("es-MX", {
            year: "numeric",
            month: "short",
            day: "numeric",
        });
    } catch {
        return dateValue;
    }
}



function isOverdue(dateValue, status) {
    if (!dateValue) return false;
    if (status === "terminada") return false;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const due = new Date(dateValue);
    due.setHours(0, 0, 0, 0);

    return due < today;
}

function isToday(dateValue) {
    if (!dateValue) return false;
    const today = new Date();
    const d = new Date(dateValue);

    return (
        today.getFullYear() === d.getFullYear() &&
        today.getMonth() === d.getMonth() &&
        today.getDate() === d.getDate()
    );
}

function isTomorrow(dateValue) {
    if (!dateValue) return false;

    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);

    const d = new Date(dateValue);

    return (
        tomorrow.getFullYear() === d.getFullYear() &&
        tomorrow.getMonth() === d.getMonth() &&
        tomorrow.getDate() === d.getDate()
    );
}

function getStatusRank(task) {
    if (isTaskInReview(task)) return 1;
    if (isTaskCorrection(task)) return 2;
    if (isOverdue(task.due_date, task.status)) return 3;
    if (isToday(task.due_date) && task.status !== "terminada") return 4;
    if (isTomorrow(task.due_date) && task.status !== "terminada") return 5;

    switch (task.status) {
        case "en_proceso":
            return 6;
        case "pendiente":
            return 7;
        case "bloqueada":
            return 8;
        case "terminada":
            return 9;
        default:
            return 10;
    }
}

function sortTasksSmart(a, b) {
    const statusRankDiff = getStatusRank(a) - getStatusRank(b);
    if (statusRankDiff !== 0) return statusRankDiff;

    const priorityRankDiff = getPriorityRank(a.priority) - getPriorityRank(b.priority);
    if (priorityRankDiff !== 0) return priorityRankDiff;

    const aDue = a.due_date ? new Date(a.due_date).getTime() : Number.MAX_SAFE_INTEGER;
    const bDue = b.due_date ? new Date(b.due_date).getTime() : Number.MAX_SAFE_INTEGER;
    if (aDue !== bDue) return aDue - bDue;

    const aUpdated = a.updated_at ? new Date(a.updated_at).getTime() : 0;
    const bUpdated = b.updated_at ? new Date(b.updated_at).getTime() : 0;
    if (aUpdated !== bUpdated) return bUpdated - aUpdated;

    return Number(b.id || 0) - Number(a.id || 0);
}


function TaskCard({ task, tasks, onOpen }) {
    const statusStyle = getStatusStyle(task.status);
    const reviewStyle = getReviewStyle(task.review_status);
    const priorityStyle = getPriorityStyle(task.priority);
    const childTasks = getChildTasks(task.id, tasks);
    const routeStyle = getRouteStyle(task, tasks);
    const dependencyTask = getDependencyTask(task, tasks);
    const evidenceLabel = getEvidenceDisplay(task);
    const suggestedAction = getSuggestedAction(task, tasks);
    const attentionReasons = getAttentionReasons(task, tasks);
    const progress = Math.max(0, Math.min(100, Number(task.progress_percent || 0)));
    const actionDetail = getSuggestedActionDetail(task, tasks);

    const actionTone = PMO_BADGE_TONES[suggestedAction.tone] || PMO_BADGE_TONES.neutral;

    return (
        <button
            type="button"
            onClick={() => onOpen(task)}
            style={{
                width: "100%",
                height: "100%",
                textAlign: "left",
                background: "#fff",
                border: `1px solid ${actionTone.border || "#e5e7eb"}`,
                borderLeft: `5px solid ${actionTone.color || "#2563eb"}`,
                borderRadius: 22,
                padding: 18,
                boxShadow: "0 14px 34px rgba(15, 23, 42, 0.065)",
                cursor: "pointer",
                transition: "transform .18s ease, box-shadow .18s ease, border-color .18s ease",
            }}
            onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow = "0 20px 44px rgba(15, 23, 42, 0.10)";
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "0 14px 34px rgba(15, 23, 42, 0.065)";
            }}
        >
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start" }}>
                <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
                        <span style={{ color: "#2563eb", fontSize: 11, fontWeight: 950, letterSpacing: ".05em", textTransform: "uppercase" }}>
                            {task.wbs_code || "SIN WBS"}
                        </span>
                        <StatusBadge tone={suggestedAction.tone}>{suggestedAction.label}</StatusBadge>
                        <StatusBadge tone={getWorkRoleTone(task)}>{getWorkRoleLabel(task)}</StatusBadge>
                    </div>

                    <div style={{ color: "#0f172a", fontSize: 16, fontWeight: 950, lineHeight: 1.25 }}>
                        {task.name || "Sin nombre"}
                    </div>

                    <div style={{ marginTop: 6, color: "#64748b", fontSize: 12, fontWeight: 800, lineHeight: 1.35 }}>
                        {task.project_name || "Sin proyecto"}
                    </div>
                </div>

                <StatusBadge bg={statusStyle.bg} color={statusStyle.color} title={getMyWorkTooltip(statusStyle.label)}>
                    {statusStyle.label}
                </StatusBadge>
            </div>

            <div
                style={{
                    marginTop: 14,
                    padding: 13,
                    borderRadius: 16,
                    background: "#ffffff",
                    border: `1px solid ${actionTone.border || "#e5e7eb"}`,
                }}
            >
                <div style={{ color: actionTone.color || "#2563eb", fontSize: 11, fontWeight: 950, textTransform: "uppercase", letterSpacing: ".045em" }}>
                    Lectura PMO
                </div>
                <div style={{ marginTop: 5, color: "#334155", fontSize: 13, fontWeight: 800, lineHeight: 1.45 }}>
                    {actionDetail}
                </div>
            </div>

            {attentionReasons.length ? (
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 12 }}>
                    {attentionReasons.map((reason) => (
                        <StatusBadge
                            key={reason}
                            tone={reason.includes("bloqueada") || reason.includes("Vencida") ? "danger" : reason.includes("revision") ? "purple" : "warning"}
                        >
                            {reason}
                        </StatusBadge>
                    ))}
                </div>
            ) : null}

            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 10, marginTop: 14 }}>
                <MiniInfoTile label="Responsable" value={getOwnerDisplay(task)} tone="info" />
                <MiniInfoTile label="Revisor" value={getReviewerDisplay(task)} tone="purple" />
                <MiniInfoTile label="Ruta" value={routeStyle.label} tone={isTaskBlocked(task, tasks) ? "danger" : "success"} />
                <MiniInfoTile label="Evidencia" value={evidenceLabel} tone={getEvidenceToneForTask(task)} />
            </div>

            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 13 }}>
                <StatusBadge bg={priorityStyle.bg} color={priorityStyle.color}>{priorityStyle.label}</StatusBadge>
                <StatusBadge bg={reviewStyle.bg} color={reviewStyle.color}>{reviewStyle.label}</StatusBadge>
                <StatusBadge tone={getDueTone(task)}>{formatDate(task.due_date)}</StatusBadge>
                {dependencyTask ? (
                    <StatusBadge tone="info">Depende de {dependencyTask.wbs_code || dependencyTask.id}</StatusBadge>
                ) : null}
                {childTasks.length ? (
                    <StatusBadge tone="warning">{childTasks.length} hija(s)</StatusBadge>
                ) : null}
            </div>

            <div style={{ marginTop: 15 }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 10, marginBottom: 7 }}>
                    <span style={{ color: "#64748b", fontSize: 11, fontWeight: 950, textTransform: "uppercase" }}>Avance</span>
                    <span style={{ color: "#0f172a", fontSize: 12, fontWeight: 950 }}>{progress}%</span>
                </div>
                <div style={{ height: 9, borderRadius: 999, background: "#e5e7eb", overflow: "hidden" }}>
                    <div
                        style={{
                            width: `${progress}%`,
                            height: "100%",
                            borderRadius: 999,
                            background: actionTone.color || "#2563eb",
                            transition: "width 0.2s ease",
                        }}
                    />
                </div>
            </div>
        </button>
    );
}


function getGridSignalCounts(tasks = []) {
    const list = Array.isArray(tasks) ? tasks : [];

    return {
        total: list.length,
        review: list.filter((task) => isTaskInReview(task)).length,
        corrections: list.filter((task) => isTaskCorrection(task)).length,
        overdue: list.filter((task) => isOverdue(task.due_date, task.status)).length,
        blocked: list.filter((task) => isTaskBlocked(task, list)).length,
    };
}

function GridSignal({ label, value, tone = "neutral" }) {
    const s = PMO_BADGE_TONES[tone] || PMO_BADGE_TONES.neutral;

    return (
        <div
            style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                border: `1px solid ${s.border}`,
                background: "#ffffff",
                color: s.color,
                borderRadius: 999,
                padding: "7px 10px",
                fontSize: 12,
                fontWeight: 950,
                whiteSpace: "nowrap",
            }}
            title={`${label}: ${value}`}
        >
            <span>{label}</span>
            <span
                style={{
                    minWidth: 22,
                    height: 22,
                    borderRadius: 999,
                    background: s.bg,
                    border: `1px solid ${s.border}`,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "0 7px",
                }}
            >
                {value}
            </span>
        </div>
    );
}

function StickyTd({ children, left = 0, width = 88, bg = "#fff", zIndex = 3, title = "" }) {
    return (
        <td
            title={title || ""}
            style={{
                padding: "12px 14px",
                fontSize: 13,
                color: "#374151",
                verticalAlign: "top",
                borderTop: "1px solid #e5e7eb",
                position: "sticky",
                left,
                width,
                minWidth: width,
                maxWidth: width,
                zIndex,
                background: bg,
                boxShadow: left > 0 ? "8px 0 14px rgba(15, 23, 42, 0.04)" : "none",
            }}
        >
            {children}
        </td>
    );
}

function StickySortableTh({ children, column, sortBy, sortDir, onSortChange, left = 0, width = 88, zIndex = 5, title = "" }) {
    const active = sortBy === column;

    return (
        <th
            title={title || "Ordenar columna"}
            onClick={() => onSortChange(column)}
            style={{
                textAlign: "left",
                padding: "12px 14px",
                fontSize: 12,
                color: active ? "#111827" : "#6b7280",
                fontWeight: 900,
                whiteSpace: "nowrap",
                position: "sticky",
                top: 0,
                left,
                width,
                minWidth: width,
                maxWidth: width,
                zIndex,
                background: "#f8fafc",
                borderBottom: "1px solid #e5e7eb",
                cursor: "pointer",
                userSelect: "none",
                boxShadow: left > 0 ? "8px 0 14px rgba(15, 23, 42, 0.05)" : "none",
            }}
        >
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                <span>{children}</span>
                <SortArrow active={active} dir={sortDir} />
            </span>
        </th>
    );
}

function TaskGrid({ tasks, onOpen, sortBy, sortDir, onSortChange }) {
    const [hoveredRowId, setHoveredRowId] = useState(null);
    const topScrollRef = useRef(null);
    const bodyScrollRef = useRef(null);
    const syncingScrollRef = useRef(false);
    const minTableWidth = 1560;
    const signals = getGridSignalCounts(tasks);

    const syncHorizontalScroll = (source, target) => {
        if (!source || !target || syncingScrollRef.current) return;
        syncingScrollRef.current = true;
        target.scrollLeft = source.scrollLeft;
        window.requestAnimationFrame(() => {
            syncingScrollRef.current = false;
        });
    };

    return (
        <div
            style={{
                border: "1px solid #e5e7eb",
                borderRadius: 22,
                background: "#fff",
                boxShadow: "0 18px 42px rgba(15, 23, 42, 0.06)",
                overflow: "hidden",
            }}
        >
            <div
                style={{
                    padding: "14px 16px",
                    borderBottom: "1px solid #e5e7eb",
                    background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 12,
                    flexWrap: "wrap",
                    alignItems: "center",
                }}
            >
                <div>
                    <div style={{ fontSize: 15, color: "#0f172a", fontWeight: 950 }}>
                        Grid PMO operativo
                    </div>
                    <div style={{ marginTop: 3, color: "#64748b", fontSize: 12, fontWeight: 750 }}>
                        Da clic en cualquier renglón para abrir la ficha PRO de la tarea.
                    </div>
                </div>

                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
                    <GridSignal label="Visibles" value={signals.total} tone="total" />
                    <GridSignal label="En revisión" value={signals.review} tone="purple" />
                    <GridSignal label="Correcciones" value={signals.corrections} tone="warning" />
                    <GridSignal label="Vencidas" value={signals.overdue} tone="danger" />
                    <GridSignal label="Bloqueadas" value={signals.blocked} tone="danger" />
                </div>
            </div>

            <div
                ref={topScrollRef}
                onScroll={(e) => syncHorizontalScroll(e.currentTarget, bodyScrollRef.current)}
                style={{
                    overflowX: "auto",
                    overflowY: "hidden",
                    height: 16,
                    borderBottom: "1px solid #e5e7eb",
                    background: "#f8fafc",
                }}
                title="Scroll horizontal superior"
            >
                <div style={{ width: minTableWidth, height: 1 }} />
            </div>

            <div
                ref={bodyScrollRef}
                onScroll={(e) => syncHorizontalScroll(e.currentTarget, topScrollRef.current)}
                style={{
                    width: "100%",
                    overflowX: "auto",
                    maxHeight: 650,
                    overflowY: "auto",
                    background: "#fff",
                }}
            >
                <table
                    style={{
                        width: "100%",
                        borderCollapse: "separate",
                        borderSpacing: 0,
                        minWidth: minTableWidth,
                    }}
                >
                    <thead>
                        <tr style={{ background: "#f8fafc" }}>
                            <StickySortableTh column="wbs" sortBy={sortBy} sortDir={sortDir} onSortChange={onSortChange} left={0} width={88} zIndex={7}>WBS</StickySortableTh>
                            <StickySortableTh column="name" sortBy={sortBy} sortDir={sortDir} onSortChange={onSortChange} left={88} width={320} zIndex={6}>Tarea</StickySortableTh>
                            <SortableTh sticky column="action" sortBy={sortBy} sortDir={sortDir} onSortChange={onSortChange} title="Acción recomendada según tu rol, el estado operativo y la revisión de la tarea.">Acción PMO</SortableTh>
                            <SortableTh sticky column="owner" sortBy={sortBy} sortDir={sortDir} onSortChange={onSortChange} title="Persona asignada para ejecutar y documentar el avance.">Responsable</SortableTh>
                            <SortableTh sticky column="reviewer" sortBy={sortBy} sortDir={sortDir} onSortChange={onSortChange} title="Persona asignada para validar, aprobar o solicitar cambios.">Revisor</SortableTh>
                            <SortableTh sticky column="status" sortBy={sortBy} sortDir={sortDir} onSortChange={onSortChange}>Estatus</SortableTh>
                            <SortableTh sticky column="review" sortBy={sortBy} sortDir={sortDir} onSortChange={onSortChange}>Revisión</SortableTh>
                            <SortableTh sticky column="route" sortBy={sortBy} sortDir={sortDir} onSortChange={onSortChange}>Ruta</SortableTh>
                            <SortableTh sticky column="evidence" sortBy={sortBy} sortDir={sortDir} onSortChange={onSortChange} title="Estado documental: evidencia registrada, pendiente, geográfica o sin alerta.">Evidencia</SortableTh>
                            <SortableTh sticky column="progress" sortBy={sortBy} sortDir={sortDir} onSortChange={onSortChange}>Avance</SortableTh>
                            <SortableTh sticky column="due_date" sortBy={sortBy} sortDir={sortDir} onSortChange={onSortChange} title="Fecha compromiso de la tarea y presión de agenda asociada.">Vence</SortableTh>
                            <SortableTh sticky column="project" sortBy={sortBy} sortDir={sortDir} onSortChange={onSortChange}>Proyecto</SortableTh>
                        </tr>
                    </thead>

                    <tbody>
                        {tasks.map((task, index) => {
                            const statusStyle = getStatusStyle(task.status);
                            const reviewStyle = getReviewStyle(task.review_status);
                            const routeStyle = getRouteStyle(task, tasks);
                            const evidenceLabel = getEvidenceDisplay(task);
                            const suggestedAction = getSuggestedAction(task, tasks);
                            const actionTone = PMO_BADGE_TONES[suggestedAction.tone] || PMO_BADGE_TONES.neutral;
                            const progress = Math.max(0, Math.min(100, Number(task.progress_percent || 0)));
                            const rowActive = hoveredRowId === task.id;

                            const rowBg = rowActive
                                ? "#f8fafc"
                                : index % 2 === 0
                                    ? "#ffffff"
                                    : "#fbfdff";

                            return (
                                <tr
                                    key={task.id}
                                    onMouseEnter={() => setHoveredRowId(task.id)}
                                    onMouseLeave={() => setHoveredRowId(null)}
                                    onClick={() => onOpen(task)}
                                    title="Abrir ficha operativa de la tarea"
                                    style={{
                                        background: rowBg,
                                        transition: "background 0.15s ease",
                                        cursor: "pointer",
                                    }}
                                >
                                    <StickyTd left={0} width={88} bg={rowBg} zIndex={4}>
                                        <span style={{ color: "#2563eb", fontWeight: 950 }}>{task.wbs_code || "—"}</span>
                                    </StickyTd>
                                    <StickyTd left={88} width={320} bg={rowBg} zIndex={3}>
                                        <div style={{ fontWeight: 900, color: "#111827", maxWidth: 290 }}>
                                            {task.name || "Sin nombre"}
                                            {hasChildTasks(task.id, tasks) ? (
                                                <span style={{ marginLeft: 6 }}>
                                                    <StatusBadge tone="warning" title={getMyWorkTooltip("Paquete WBS")}>Paquete WBS</StatusBadge>
                                                </span>
                                            ) : null}
                                        </div>
                                        <div style={{ marginTop: 5, color: "#64748b", fontSize: 12, maxWidth: 300, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                            {getSuggestedActionDetail(task, tasks)}
                                        </div>
                                    </StickyTd>
                                    <Td title="Acción sugerida para esta tarea según tu rol, estado y revisión.">
                                        <StatusBadge tone={suggestedAction.tone}>{suggestedAction.label}</StatusBadge>
                                        <div style={{ marginTop: 6 }}>
                                            <StatusBadge tone={getWorkRoleTone(task)}>{getWorkRoleLabel(task)}</StatusBadge>
                                        </div>
                                    </Td>
                                    <Td title="Responsable asignado para ejecutar y documentar el avance.">{getOwnerDisplay(task)}</Td>
                                    <Td title="Revisor asignado para validar, aprobar o solicitar cambios.">{getReviewerDisplay(task)}</Td>
                                    <Td>
                                        <StatusBadge bg={statusStyle.bg} color={statusStyle.color}>{statusStyle.label}</StatusBadge>
                                    </Td>
                                    <Td>
                                        <StatusBadge bg={reviewStyle.bg} color={reviewStyle.color}>{reviewStyle.label}</StatusBadge>
                                    </Td>
                                    <Td>
                                        <StatusBadge bg={routeStyle.bg} color={routeStyle.color}>{routeStyle.label}</StatusBadge>
                                    </Td>
                                    <Td>
                                        <StatusBadge tone={getEvidenceToneForTask(task)}>{evidenceLabel}</StatusBadge>
                                    </Td>
                                    <Td>
                                        <div style={{ minWidth: 120 }}>
                                            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5 }}>
                                                <span style={{ fontSize: 11, color: "#64748b", fontWeight: 900 }}>{progress}%</span>
                                            </div>
                                            <div style={{ height: 8, borderRadius: 999, background: "#e5e7eb", overflow: "hidden" }}>
                                                <div style={{ width: `${progress}%`, height: "100%", borderRadius: 999, background: actionTone.color || "#2563eb" }} />
                                            </div>
                                        </div>
                                    </Td>
                                    <Td>
                                        <StatusBadge tone={getDueTone(task)}>{formatDate(task.due_date)}</StatusBadge>
                                    </Td>
                                    <Td>{task.project_name || "Sin proyecto"}</Td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function SortableTh({ children, sticky = false, column, sortBy, sortDir, onSortChange, title = "" }) {
    const active = sortBy === column;

    return (
        <th
            title={title || "Ordenar columna"}
            onClick={() => onSortChange(column)}
            style={{
                textAlign: "left",
                padding: "12px 14px",
                fontSize: 12,
                color: active ? "#111827" : "#6b7280",
                fontWeight: 800,
                whiteSpace: "nowrap",
                position: sticky ? "sticky" : "static",
                top: 0,
                zIndex: 2,
                background: "#f8fafc",
                borderBottom: "1px solid #e5e7eb",
                cursor: "pointer",
                userSelect: "none",
            }}
        >
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                <span>{children}</span>
                <SortArrow active={active} dir={sortDir} />
            </span>
        </th>
    );
}

function SortArrow({ active, dir }) {
    if (!active) {
        return <span style={{ fontSize: 10, color: "#cbd5e1" }}>↕</span>;
    }

    return (
        <span style={{ fontSize: 10, color: "#111827" }}>
            {dir === "asc" ? "↑" : "↓"}
        </span>
    );
}
function Th({ children, sticky = false }) {
    return (
        <th
            style={{
                textAlign: "left",
                padding: "12px 14px",
                fontSize: 12,
                color: "#6b7280",
                fontWeight: 800,
                whiteSpace: "nowrap",
                position: sticky ? "sticky" : "static",
                top: 0,
                zIndex: 2,
                background: "#f8fafc",
                borderBottom: "1px solid #e5e7eb",
            }}
        >
            {children}
        </th>
    );
}

function Td({ children, title = "" }) {
    return (
        <td
            title={title || ""}
            style={{
                padding: "12px 14px",
                fontSize: 13,
                color: "#374151",
                verticalAlign: "top",
                borderTop: "1px solid #e5e7eb",
            }}
        >
            {children}
        </td>
    );
}
function MiniAction({
    children,
    onClick,
    bg = "#111827",
    color = "#fff",
    border = "none",
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            style={{
                padding: "7px 10px",
                borderRadius: 8,
                border,
                background: bg,
                color,
                cursor: "pointer",
                fontWeight: 700,
                fontSize: 12,
                whiteSpace: "nowrap",
            }}
        >
            {children}
        </button>
    );
}
function compareText(a, b) {
    return String(a || "").localeCompare(String(b || ""), "es", { sensitivity: "base" });
}

function compareWbs(a, b) {
    const parse = (value) =>
        String(value || "")
            .split(".")
            .map((part) => {
                const n = Number(part);
                return Number.isFinite(n) ? n : part;
            });

    const aa = parse(a);
    const bb = parse(b);
    const max = Math.max(aa.length, bb.length);

    for (let i = 0; i < max; i += 1) {
        const x = aa[i];
        const y = bb[i];

        if (x === undefined) return -1;
        if (y === undefined) return 1;

        if (typeof x === "number" && typeof y === "number") {
            if (x !== y) return x - y;
        } else {
            const result = String(x).localeCompare(String(y), "es", {
                numeric: true,
                sensitivity: "base",
            });
            if (result !== 0) return result;
        }
    }

    return 0;
}

function compareNumber(a, b) {
    return Number(a || 0) - Number(b || 0);
}

function compareDate(a, b) {
    const aTime = a ? new Date(a).getTime() : Number.MAX_SAFE_INTEGER;
    const bTime = b ? new Date(b).getTime() : Number.MAX_SAFE_INTEGER;
    return aTime - bTime;
}

function getReviewRank(reviewStatus) {
    switch ((reviewStatus || "").toLowerCase()) {
        case "en_revision":
        case "pendiente_revision":
            return 1;
        case "con_cambios":
            return 2;
        case "rechazado":
            return 3;
        case "aprobado":
            return 4;
        case "borrador":
            return 5;
        default:
            return 6;
    }
}

function getStatusOrderRank(status) {
    switch ((status || "").toLowerCase()) {
        case "pendiente":
            return 1;
        case "en_proceso":
            return 2;
        case "bloqueada":
            return 3;
        case "terminada":
            return 4;
        case "cancelada":
            return 5;
        default:
            return 6;
    }
}

function sortTasksByColumn(tasks, sortBy, sortDir) {
    const sorted = [...tasks];

    sorted.sort((a, b) => {
        let result = 0;

        switch (sortBy) {
            case "wbs":
                result = compareWbs(a.wbs_code, b.wbs_code);
                break;
            case "name":
                result = compareText(a.name, b.name);
                break;
            case "project":
                result = compareText(a.project_name, b.project_name);
                break;
            case "owner":
                result = compareText(getOwnerDisplay(a), getOwnerDisplay(b));
                break;
            case "reviewer":
                result = compareText(getReviewerDisplay(a), getReviewerDisplay(b));
                break;
            case "action":
                result = compareText(getSuggestedAction(a, tasks).label, getSuggestedAction(b, tasks).label);
                break;
            case "route":
                result = compareText(getRouteStyle(a, tasks).label, getRouteStyle(b, tasks).label);
                break;
            case "evidence":
                result = compareText(getEvidenceDisplay(a), getEvidenceDisplay(b));
                break;
            case "priority":
                result = getPriorityRank(a.priority) - getPriorityRank(b.priority);
                break;
            case "status":
                result = getStatusOrderRank(a.status) - getStatusOrderRank(b.status);
                break;
            case "review":
                result = getReviewRank(a.review_status) - getReviewRank(b.review_status);
                break;
            case "progress":
                result = compareNumber(a.progress_percent, b.progress_percent);
                break;
            case "due_date":
                result = compareDate(a.due_date, b.due_date);
                break;
            case "smart":
            default:
                result = sortTasksSmart(a, b);
                break;
        }

        return sortDir === "desc" ? result * -1 : result;
    });

    return sorted;
}

const buildAuthHeaders = (extra = {}) => {
    const token =
        localStorage.getItem("cani_auth") ||
        localStorage.getItem("token") ||
        "";

    return {
        ...extra,
        Authorization: token ? `Bearer ${token}` : "",
        "X-CANI-AUTH": token || "",
    };
};

const emptyBoxStyle = {
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 16,
    padding: 24,
    color: "#6b7280",
};

function shouldShowEvidenceWarnings(task) {
    const status = String(task?.status || "").trim().toLowerCase();
    const reviewStatus = String(task?.review_status || "").trim().toLowerCase();

    if (["terminada", "cancelada"].includes(status)) {
        return false;
    }

    if (reviewStatus === "aprobado") {
        return false;
    }

    return true;
}

function getTaskOpenMode(task, requestedMode = "") {
    const status = String(task?.status || "").toLowerCase();
    const reviewStatus = String(task?.review_status || "").toLowerCase();
    const workRole = String(task?.work_role || "").toLowerCase();

    const isReviewer =
        workRole === "revisor" ||
        workRole === "responsable_y_revisor" ||
        Number(task?.is_review_assignment || 0) === 1;

    const isOwner =
        workRole === "responsable" ||
        workRole === "responsable_y_revisor" ||
        Number(task?.is_owner_assignment || 0) === 1;

    if (["terminada", "cancelada"].includes(status)) {
        return "view";
    }

    if (
        isReviewer &&
        ["en_revision", "pendiente_revision"].includes(reviewStatus)
    ) {
        return "review";
    }

    if (
        isOwner &&
        ["rechazado", "con_cambios"].includes(reviewStatus)
    ) {
        return "progress";
    }

    if (
        isOwner &&
        ["pendiente", "en_proceso", "bloqueada"].includes(status) &&
        !["en_revision", "pendiente_revision"].includes(reviewStatus)
    ) {
        return "progress";
    }

    if (["progress", "review", "view"].includes(requestedMode)) {
        return requestedMode;
    }

    return "view";
}

export default function MyWorkPage() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filter, setFilter] = useState(() => searchParams.get("filter") || "todas");
    const [roleFilter, setRoleFilter] = useState(() => searchParams.get("role") || "todo");
    const [search, setSearch] = useState("");
    const [selectedProjectId, setSelectedProjectId] = useState(
        () => searchParams.get("project_id") || "todos"
    );
    const [viewMode, setViewMode] = useState(
        localStorage.getItem("mywork_view_mode") || "grid"
    );
    const reviewSectionRef = useRef(null);
    const correctionsSectionRef = useRef(null);
    const requestedCorrectionsSectionRef = useRef(null);
    const overdueSectionRef = useRef(null);
    const todaySectionRef = useRef(null);
    const tomorrowSectionRef = useRef(null);
    const noDateSectionRef = useRef(null);
    const pendingSectionRef = useRef(null);
    const completedSectionRef = useRef(null);
    //const isSegmentedOverview = filter === "todas";
    const [sortBy, setSortBy] = useState("wbs");
    const [sortDir, setSortDir] = useState("asc");

    const handleSortChange = (column) => {
        if (sortBy === column) {
            setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
            return;
        }

        setSortBy(column);
        setSortDir(column === "smart" ? "asc" : "asc");
    };

    function scrollToSection(ref) {
        if (ref?.current) {
            ref.current.scrollIntoView({
                behavior: "smooth",
                block: "start",
            });
        }
    }


    function ViewModeToggle({ value, onChange }) {
        return (
            <div
                style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 4,
                    padding: 4,
                    border: "1px solid #d1d5db",
                    borderRadius: 12,
                    background: "#fff",
                    boxShadow: "0 2px 8px rgba(15, 23, 42, 0.05)",
                }}
            >
                <IconToggleButton
                    active={value === "grid"}
                    onClick={() => onChange("grid")}
                    title="Vista grid"
                >
                    <GridLinesIcon />
                </IconToggleButton>

                <IconToggleButton
                    active={value === "cards"}
                    onClick={() => onChange("cards")}
                    title="Vista cards"
                >
                    <CardsIcon />
                </IconToggleButton>
            </div>
        );
    }

    function IconToggleButton({ active, onClick, children, title }) {
        return (
            <button
                type="button"
                onClick={onClick}
                title={title}
                style={{
                    width: 40,
                    height: 40,
                    borderRadius: 10,
                    border: active ? "1px solid #111827" : "1px solid transparent",
                    background: active ? "#111827" : "#fff",
                    color: active ? "#fff" : "#374151",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                }}
            >
                {children}
            </button>
        );
    }

    function GridLinesIcon() {
        return (
            <svg width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                <path d="M4 5.5H16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                <path d="M4 10H16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                <path d="M4 14.5H16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
        );
    }

    function CardsIcon() {
        return (
            <svg width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                <rect x="3" y="3" width="5.5" height="5.5" rx="1.2" stroke="currentColor" strokeWidth="1.5" />
                <rect x="11.5" y="3" width="5.5" height="5.5" rx="1.2" stroke="currentColor" strokeWidth="1.5" />
                <rect x="3" y="11.5" width="5.5" height="5.5" rx="1.2" stroke="currentColor" strokeWidth="1.5" />
                <rect x="11.5" y="11.5" width="5.5" height="5.5" rx="1.2" stroke="currentColor" strokeWidth="1.5" />
            </svg>
        );
    }


    const token =
        localStorage.getItem("cani_auth") ||
        localStorage.getItem("token") ||
        "";

    const loggedUserId =
        Number(localStorage.getItem("user_id")) ||
        Number(localStorage.getItem("id")) ||
        Number(localStorage.getItem("cani_user_id")) ||
        0;

    const loadMyTasks = async () => {
        try {
            setLoading(true);

            const possibleUserId =
                Number(localStorage.getItem("user_id")) ||
                Number(localStorage.getItem("id")) ||
                Number(localStorage.getItem("cani_user_id")) ||
                0;

            let url = `${API_BASE}/my_tasks.php`;

            if (possibleUserId) {
                url += `?user_id=${possibleUserId}`;
            }

            const response = await fetch(url, {
                headers: buildAuthHeaders(),
            });

            const txt = await response.text();

            if (!txt || !txt.trim()) {
                throw new Error(`Respuesta vacía (HTTP ${response.status})`);
            }

            let result;
            try {
                result = JSON.parse(txt);
            } catch (e) {
                throw new Error(`Respuesta no JSON: ${txt}`);
            }

            if (!response.ok || !result.success) {
                throw new Error(result.detail || result.message || "No se pudieron cargar las tareas");
            }

            setTasks(Array.isArray(result.data) ? result.data : []);
        } catch (error) {
            console.error(error);
            alert(error.message || "Error al cargar tareas");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadMyTasks();
    }, []);

    useEffect(() => {
        localStorage.setItem("mywork_view_mode", viewMode);
    }, [viewMode]);

    useEffect(() => {
        const qpFilter = searchParams.get("filter") || "todas";
        if (qpFilter !== filter) {
            setFilter(qpFilter);
        }
    }, [searchParams]);

    useEffect(() => {
        const next = new URLSearchParams(searchParams);
        if (filter && filter !== "todas") {
            next.set("filter", filter);
        } else {
            next.delete("filter");
        }
        setSearchParams(next, { replace: true });
    }, [filter]);

    useEffect(() => {
        const qpRole = searchParams.get("role") || "todo";
        if (qpRole !== roleFilter) {
            setRoleFilter(qpRole);
        }
    }, [searchParams]);

    useEffect(() => {
        const next = new URLSearchParams(searchParams);
        if (roleFilter && roleFilter !== "todo") {
            next.set("role", roleFilter);
        } else {
            next.delete("role");
        }
        setSearchParams(next, { replace: true });
    }, [roleFilter]);

    const handleOpen = (task, mode = "") => {
        const safeMode = getTaskOpenMode(task, mode);

        navigate(`/my-work/task/${task.id}?mode=${safeMode}`, {
            state: {
                openTaskId: task.id,
                taskMode: safeMode,
                fromMyWork: true,
                returnTo: "/my-work",
                returnLabel: "Mi trabajo",
            },
        });
    };

    const projectOptions = useMemo(() => {
        const map = new Map();

        (tasks || []).forEach((task) => {
            const projectId = String(task.project_id || "");
            const projectName = task.project_name || "Sin proyecto";

            if (projectId) {
                map.set(projectId, projectName);
            }
        });

        return Array.from(map.entries())
            .map(([value, label]) => ({ value, label }))
            .sort((a, b) => a.label.localeCompare(b.label, "es"));
    }, [tasks]);

    const scopedTasks = useMemo(() => {
        const text = search.trim().toLowerCase();

        return tasks.filter((task) => {
            const matchSearch =
                !text ||
                (task.name || "").toLowerCase().includes(text) ||
                (task.project_name || "").toLowerCase().includes(text) ||
                (task.wbs_code || "").toLowerCase().includes(text);

            const matchProject =
                selectedProjectId === "todos" ||
                String(task.project_id || "") === String(selectedProjectId);

            const matchRole =
                roleFilter === "todo"
                    ? true
                    : roleFilter === "responsable"
                        ? isOwnerAssignment(task)
                        : roleFilter === "revisor"
                            ? isReviewerAssignment(task)
                            : true;

            return matchSearch && matchProject && matchRole;
        });
    }, [tasks, search, selectedProjectId, roleFilter]);

    const filteredTasks = useMemo(() => {
        return scopedTasks.filter((task) => {
            const matchFilter =
                filter === "todas"
                    ? true
                    : filter === "activas"
                        ? !["terminada", "cancelada"].includes(String(task.status || "").toLowerCase())
                        : filter === "criticas"
                            ? String(task.priority || "").toLowerCase() === "critica" && !["terminada", "cancelada"].includes(String(task.status || "").toLowerCase())
                            : filter === "revision"
                                ? isTaskInReview(task)
                                : filter === "correcciones"
                                    ? isTaskCorrectionForOwner(task)
                                    : filter === "correcciones_solicitadas"
                                        ? isTaskCorrectionRequestedByMe(task)
                                        : filter === "bloqueadas"
                                            ? isTaskBlocked(task, scopedTasks) &&
                                            task.status !== "terminada" &&
                                            task.status !== "cancelada"
                                            : filter === "vencidas"
                                                ? isOverdue(task.due_date, task.status)
                                                : filter === "hoy"
                                                    ? isToday(task.due_date) &&
                                                    task.status !== "terminada"
                                                    : filter === "manana"
                                                        ? isTomorrow(task.due_date) &&
                                                        task.status !== "terminada"
                                                        : filter === "sin_fecha"
                                                            ? !task.due_date &&
                                                            task.status !== "terminada"
                                                            : filter === "terminada"
                                                                ? task.status === "terminada"
                                                                : task.status === filter;

            return matchFilter;
        });
    }, [scopedTasks, filter]);



    const orderedTasks = useMemo(() => {
        const base = [...filteredTasks];

        if (sortBy === "smart") {
            return base.sort(sortTasksSmart);
        }

        return sortTasksByColumn(base, sortBy, sortDir);
    }, [filteredTasks, sortBy, sortDir]);

    const isSegmentedOverview = filter === "todas";

    const filteredSectionTitle = (() => {
        switch (filter) {
            case "activas":
                return "Resultados: Tareas activas";
            case "criticas":
                return "Resultados: Tareas críticas";
            case "pendiente":
                return "Resultados: Pendientes";
            case "en_proceso":
                return "Resultados: En proceso";
            case "revision":
                return "Resultados: En revisión";
            case "correcciones":
                return "Resultados: Correcciones";
            case "correcciones_solicitadas":
                return "Resultados: Correcciones solicitadas";
            case "bloqueadas":
                return "Resultados: Bloqueadas";
            case "vencidas":
                return "Resultados: Vencidas";
            case "hoy":
                return "Resultados: Vence hoy";
            case "manana":
                return "Resultados: Vence mañana";
            case "sin_fecha":
                return "Resultados: Sin fecha";
            case "terminada":
                return "Resultados: Terminadas";
            default:
                return "Resultados del filtro";
        }
    })();

    const filteredSectionSubtitle = `Mostrando ${filteredTasks.length} tarea(s) para el filtro seleccionado.`;

    const reviewTasks = useMemo(
        () => orderedTasks.filter(isTaskInReview),
        [orderedTasks]
    );

    const correctionTasks = useMemo(
        () => orderedTasks.filter(isTaskCorrectionForOwner),
        [orderedTasks]
    );

    const requestedCorrectionTasks = useMemo(
        () => orderedTasks.filter(isTaskCorrectionRequestedByMe),
        [orderedTasks]
    );

    const overdueTasks = useMemo(
        () => orderedTasks.filter((t) => isOverdue(t.due_date, t.status)),
        [orderedTasks]
    );

    const todayTasks = useMemo(
        () => orderedTasks.filter((t) => isToday(t.due_date) && t.status !== "terminada"),
        [orderedTasks]
    );

    const tomorrowTasks = useMemo(
        () => orderedTasks.filter((t) => isTomorrow(t.due_date) && t.status !== "terminada"),
        [orderedTasks]
    );

    const noDateTasks = useMemo(
        () => orderedTasks.filter((t) => !t.due_date && t.status !== "terminada"),
        [orderedTasks]
    );

    const blockedTasks = useMemo(
        () =>
            orderedTasks.filter(
                (t) =>
                    isTaskBlocked(t, tasks) &&
                    t.status !== "terminada" &&
                    t.status !== "cancelada"
            ),
        [orderedTasks, tasks]
    );

    const pendingTasks = useMemo(
        () =>
            orderedTasks.filter(
                (t) =>
                    !isTaskInReview(t) &&
                    !isTaskCorrectionForOwner(t) &&
                    !isTaskCorrectionRequestedByMe(t) &&
                    !isTaskBlocked(t, tasks) &&
                    !isOverdue(t.due_date, t.status) &&
                    !isToday(t.due_date) &&
                    !isTomorrow(t.due_date) &&
                    !!t.due_date &&
                    t.status !== "terminada" &&
                    t.status !== "cancelada"
            ),
        [orderedTasks, tasks]
    );

    const completedTasks = useMemo(
        () => orderedTasks.filter((t) => t.status === "terminada"),
        [orderedTasks]
    );

    const summary = useMemo(() => {
        return {
            total: scopedTasks.length,
            activas: scopedTasks.filter((t) => !["terminada", "cancelada"].includes(String(t.status || "").toLowerCase())).length,
            pendientes: scopedTasks.filter((t) => t.status === "pendiente").length,
            enProceso: scopedTasks.filter((t) => t.status === "en_proceso").length,
            revision: scopedTasks.filter(isTaskInReview).length,
            correcciones: scopedTasks.filter(isTaskCorrectionForOwner).length,
            correccionesSolicitadas: scopedTasks.filter(isTaskCorrectionRequestedByMe).length,
            bloqueadas: scopedTasks.filter((t) => isTaskBlocked(t, scopedTasks) && t.status !== "terminada" && t.status !== "cancelada").length,
            vencidas: scopedTasks.filter((t) => isOverdue(t.due_date, t.status)).length,
            hoy: scopedTasks.filter((t) => isToday(t.due_date) && t.status !== "terminada").length,
            manana: scopedTasks.filter((t) => isTomorrow(t.due_date) && t.status !== "terminada").length,
            sinFecha: scopedTasks.filter((t) => !t.due_date && t.status !== "terminada").length,
            terminadas: scopedTasks.filter((t) => t.status === "terminada").length,
            criticas: scopedTasks.filter((t) => String(t.priority || "").toLowerCase() === "critica" && !["terminada", "cancelada"].includes(String(t.status || "").toLowerCase())).length,
            enTiempo: scopedTasks.filter((t) => !["terminada", "cancelada"].includes(String(t.status || "").toLowerCase()) && !isOverdue(t.due_date, t.status) && !isTaskBlocked(t, scopedTasks) && !isTaskInReview(t) && !isTaskCorrection(t)).length,
            responsables: scopedTasks.filter(isOwnerAssignment).length,
            revisores: scopedTasks.filter(isReviewerAssignment).length,
        };
    }, [scopedTasks]);


    const activeTasksCount = summary.activas;

    const productivityRate = useMemo(
        () => summary.total > 0 ? Math.round((summary.terminadas / summary.total) * 100) : 0,
        [summary]
    );

    const flowData = useMemo(() => [
        { label: "Pendientes", value: summary.pendientes, tone: "neutral" },
        { label: "En proceso", value: summary.enProceso, tone: "info" },
        { label: "En revisión", value: summary.revision, tone: "purple" },
        { label: "Bloqueadas", value: summary.bloqueadas, tone: "danger" },
        { label: "Terminadas", value: summary.terminadas, tone: "success" },
    ], [summary]);

    const agendaData = useMemo(() => [
        { label: "Vencidas", value: summary.vencidas, tone: "danger" },
        { label: "Vence hoy", value: summary.hoy, tone: "warning" },
        { label: "Vence mañana", value: summary.manana, tone: "info" },
        { label: "Sin fecha", value: summary.sinFecha, tone: "neutral" },
    ], [summary]);

    const projectLoadData = useMemo(() => {
        const map = new Map();
        scopedTasks.forEach((task) => {
            const projectId = String(task.project_id || "sin-proyecto");
            const current = map.get(projectId) || { projectId, projectName: task.project_name || "Sin proyecto", total: 0, overdue: 0, review: 0 };
            current.total += 1;
            if (isOverdue(task.due_date, task.status)) current.overdue += 1;
            if (isTaskInReview(task)) current.review += 1;
            map.set(projectId, current);
        });
        return Array.from(map.values()).sort((a, b) => b.total - a.total);
    }, [scopedTasks]);

    const operationalInsight = useMemo(() => {
        const attentionTasks = summary.vencidas + summary.bloqueadas + summary.revision + summary.correcciones;
        if (summary.vencidas > 0 || summary.bloqueadas > 0) {
            return { tone: "danger", title: "Presión operativa alta", message: `Tu carga presenta ${summary.vencidas} tarea(s) vencida(s), ${summary.bloqueadas} bloqueada(s) y ${summary.revision} en revisión. Atiende primero vencimientos y bloqueos antes de abrir nuevas cargas.`, activeTasks: activeTasksCount, attentionTasks };
        }
        if (summary.revision > 0 || summary.correcciones > 0) {
            return { tone: "purple", title: "Flujo de revisión activo", message: `Hay ${summary.revision} tarea(s) esperando decisión y ${summary.correcciones} corrección(es) por atender. Mantener este flujo limpio evita cuellos de botella.`, activeTasks: activeTasksCount, attentionTasks };
        }
        return { tone: "success", title: "Operación estable", message: "No se detectan señales críticas dominantes. Mantén seguimiento preventivo y avanza las tareas activas por prioridad y fecha objetivo.", activeTasks: activeTasksCount, attentionTasks };
    }, [summary, activeTasksCount]);

    const priorityNowTasks = useMemo(() => {
        return [...scopedTasks]
            .filter((task) => String(task.status || "").toLowerCase() !== "terminada")
            .sort(sortTasksSmart)
            .slice(0, 6);
    }, [scopedTasks]);

    const attentionImmediateTasks = useMemo(() => {
        return orderedTasks.filter((task) =>
            isOverdue(task.due_date, task.status) ||
            isTaskBlocked(task, scopedTasks) ||
            isTaskInReview(task) ||
            isTaskCorrection(task)
        );
    }, [orderedTasks, scopedTasks]);

    const upcomingActionTasks = useMemo(() => {
        const attentionIds = new Set(attentionImmediateTasks.map((task) => String(task.id)));
        return orderedTasks.filter((task) => {
            const status = String(task.status || "").toLowerCase();
            return !attentionIds.has(String(task.id)) && !["terminada", "cancelada"].includes(status);
        });
    }, [orderedTasks, attentionImmediateTasks]);

    const consultationTasks = useMemo(() => {
        return orderedTasks.filter((task) => ["terminada", "cancelada"].includes(String(task.status || "").toLowerCase()));
    }, [orderedTasks]);

    const renderTaskCollection = (list) => {
        if (list.length === 0) {
            return <EmptyMini text="Sin tareas en esta sección. Cambia el filtro o revisa otra vista del tablero." />;
        }

        if (viewMode === "grid") {
            return (
                <TaskGrid
                    tasks={list}
                    onOpen={handleOpen}
                    sortBy={sortBy}
                    sortDir={sortDir}
                    onSortChange={handleSortChange}
                />
            );
        }
        return (
            <div
                style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(360px, 1fr))",
                    gap: 14,
                }}
            >
                {list.map((task) => (
                    <TaskCard key={task.id} task={task} tasks={tasks} onOpen={handleOpen} />
                ))}
            </div>
        );
    };

    return (
        <div
            style={{
                padding: 24,
                minHeight: "100%",
                background: "radial-gradient(circle at top left, rgba(37,99,235,.10), transparent 28%), #F8FAFC",
            }}
        >
            <InsightBanner insight={operationalInsight} />

            <div style={{
                background: "#FFFFFF",
                border: "1px solid #E2E8F0",
                borderRadius: 24,
                padding: 16,
                marginBottom: 18,
                boxShadow: "0 18px 45px rgba(15,23,42,.06)",
            }}>
                <div style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 16,
                    alignItems: "center",
                    flexWrap: "wrap",
                }}>
                    <div style={{ minWidth: 280, flex: "1 1 360px" }}>
                        <div style={{
                            color: "#64748B",
                            fontSize: 10,
                            fontWeight: 950,
                            textTransform: "uppercase",
                            letterSpacing: ".06em",
                            marginBottom: 8,
                        }}>
                            Contexto de trabajo
                        </div>
                        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                            <select
                                value={selectedProjectId}
                                onChange={(e) => setSelectedProjectId(e.target.value)}
                                title={selectedProjectId === "todos" ? getMyWorkTooltip("Portafolio PMO") : getMyWorkTooltip("Proyecto completo")}
                                style={{
                                    minWidth: 260,
                                    height: 44,
                                    borderRadius: 999,
                                    border: "1px solid #CBD5E1",
                                    padding: "0 16px",
                                    fontSize: 13,
                                    fontWeight: 900,
                                    outline: "none",
                                    background: "#fff",
                                    color: "#0F172A",
                                    boxShadow: "0 10px 22px rgba(15,23,42,.04)",
                                }}
                            >
                                <option value="todos">Portafolio PMO</option>
                                {projectOptions.map((project) => (
                                    <option key={project.value} value={project.value}>
                                        {project.label}
                                    </option>
                                ))}
                            </select>

                            <StatusBadge tone={selectedProjectId === "todos" ? "neutral" : "info"}>
                                {selectedProjectId === "todos" ? "Portafolio PMO" : "Proyecto completo"}
                            </StatusBadge>
                        </div>
                    </div>

                    <div style={{ display: "grid", gap: 8, justifyItems: "end" }}>
                        <div style={{ color: "#64748B", fontSize: 10, fontWeight: 950, textTransform: "uppercase", letterSpacing: ".06em" }}>
                            Vista por rol
                        </div>
                        <SegmentedRoleControl value={roleFilter} onChange={setRoleFilter} summary={summary} />
                    </div>
                </div>
            </div>

            <div
                style={{
                    marginBottom: 18,
                    borderRadius: 28,
                    padding: 18,
                    background: "linear-gradient(135deg, rgba(255,255,255,.86), rgba(248,250,252,.74))",
                    border: "1px solid rgba(203,213,225,.72)",
                    boxShadow: "0 24px 60px rgba(15,23,42,.075)",
                    backdropFilter: "blur(12px)",
                }}
            >
                <div style={{ display: "flex", justifyContent: "space-between", gap: 14, alignItems: "flex-end", flexWrap: "wrap", marginBottom: 14 }}>
                    <div>
                        <div style={{ color: "#64748B", fontSize: 10, fontWeight: 950, textTransform: "uppercase", letterSpacing: ".06em" }}>
                            Centro de mando operativo
                        </div>
                        <div style={{ marginTop: 5, color: "#0F172A", fontSize: 22, fontWeight: 950, letterSpacing: "-.035em" }}>
                            Prioridad crítica, vencimientos y revisión en una sola lectura
                        </div>
                        <div style={{ marginTop: 4, color: "#64748B", fontSize: 12, fontWeight: 750 }}>
                            Selecciona una métrica para filtrar el tablero. La operación se concentra en Atención inmediata y resultados.
                        </div>
                    </div>
                    <StatusBadge tone={filter === "todas" ? "neutral" : "info"}>
                        Filtro activo: {filter === "todas" ? "Todas" : filteredSectionTitle}
                    </StatusBadge>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))", gap: 14 }}>
                    <CommandMetricCard label="Tareas críticas" value={summary.criticas} icon="🔥" tone="danger" active={filter === "criticas"} onClick={() => setFilter("criticas")} helper="Prioridad crítica sin cerrar." />
                    <CommandMetricCard label="Vencen hoy" value={summary.hoy} icon="🗓️" tone="warning" active={filter === "hoy"} onClick={() => setFilter("hoy")} helper="Requieren atención durante la jornada." />
                    <CommandMetricCard label="En revisión" value={summary.revision} icon="🛡️" tone="purple" active={filter === "revision"} onClick={() => setFilter("revision")} helper="Avances esperando dictamen." />
                </div>
            </div>

            <div
                style={{
                    display: "grid",
                    gridTemplateColumns: "minmax(0, 1fr) minmax(300px, 360px)",
                    gap: 18,
                    alignItems: "start",
                    marginBottom: 18,
                }}
            >
                <PriorityNowPanel items={priorityNowTasks} allTasks={scopedTasks} onOpen={handleOpen} />
                <OperationalSidePanel
                    summary={summary}
                    flowData={flowData}
                    agendaData={agendaData}
                    projectLoadData={projectLoadData}
                    productivityRate={productivityRate}
                />
            </div>

            <div style={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 24, padding: 16, marginBottom: 18, boxShadow: "0 18px 45px rgba(15,23,42,.06)" }}>
                <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center", justifyContent: "space-between" }}>
                    <div style={{ display: "grid", gap: 8 }}>
                        <div style={{ color: "#64748B", fontSize: 10, fontWeight: 950, textTransform: "uppercase", letterSpacing: ".06em" }}>
                            Búsqueda y filtros operativos
                        </div>
                        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                            <input
                                type="text"
                                placeholder="Buscar por tarea, WBS o proyecto..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                title="Busca dentro del tablero por nombre de tarea, WBS o proyecto."
                                style={{
                                    width: 340,
                                    maxWidth: "100%",
                                    height: 40,
                                    borderRadius: 999,
                                    border: "1px solid #CBD5E1",
                                    padding: "0 16px",
                                    fontSize: 13,
                                    fontWeight: 800,
                                    outline: "none",
                                    background: "#fff",
                                    color: "#0F172A",
                                    boxShadow: "0 8px 18px rgba(15,23,42,.04)",
                                }}
                            />
                            {search ? (
                                <button
                                    type="button"
                                    onClick={() => setSearch("")}
                                    title="Limpiar búsqueda"
                                    style={{
                                        height: 40,
                                        borderRadius: 999,
                                        border: "1px solid #CBD5E1",
                                        background: "#fff",
                                        color: "#334155",
                                        padding: "0 12px",
                                        fontSize: 12,
                                        fontWeight: 900,
                                        cursor: "pointer",
                                    }}
                                >
                                    Limpiar
                                </button>
                            ) : null}
                        </div>
                        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                            <FilterButton tone="neutral" count={summary.total} active={filter === "todas"} onClick={() => setFilter("todas")} title="Muestra todas las tareas del contexto seleccionado.">Todas</FilterButton>
                            <FilterButton tone="info" count={summary.activas} active={filter === "activas"} onClick={() => setFilter("activas")} title="Tareas abiertas que requieren avance, revisión o seguimiento.">Activas</FilterButton>
                            <FilterButton tone="danger" count={summary.criticas} active={filter === "criticas"} onClick={() => setFilter("criticas")} title="Tareas con prioridad crítica que siguen abiertas.">Críticas</FilterButton>
                            <FilterButton tone="neutral" count={summary.pendientes} active={filter === "pendiente"} onClick={() => setFilter("pendiente")} title="Tareas que aún no han iniciado formalmente.">Pendientes</FilterButton>
                            <FilterButton tone="info" count={summary.enProceso} active={filter === "en_proceso"} onClick={() => setFilter("en_proceso")} title="Tareas activas disponibles para avance o seguimiento.">En proceso</FilterButton>
                            <FilterButton tone="purple" count={summary.revision} active={filter === "revision"} onClick={() => setFilter("revision")} title="Avances enviados que esperan dictamen del revisor.">Por revisar</FilterButton>
                            <FilterButton tone="warning" count={summary.correcciones} active={filter === "correcciones"} onClick={() => setFilter("correcciones")} title="Tareas donde el responsable debe corregir o reenviar.">Correcciones recibidas</FilterButton>
                            <FilterButton tone="purple" count={summary.correccionesSolicitadas} active={filter === "correcciones_solicitadas"} onClick={() => setFilter("correcciones_solicitadas")} title="Tareas donde tú solicitaste cambios como revisor.">Correcciones solicitadas</FilterButton>
                            <FilterButton tone="danger" count={summary.bloqueadas} active={filter === "bloqueadas"} onClick={() => setFilter("bloqueadas")} title="Tareas detenidas por ruta, dependencia o condición operativa.">Bloqueadas</FilterButton>
                            <FilterButton tone="danger" count={summary.vencidas} active={filter === "vencidas"} onClick={() => setFilter("vencidas")} title="Tareas con fecha compromiso vencida.">Vencidas</FilterButton>
                            <FilterButton tone="warning" count={summary.hoy} active={filter === "hoy"} onClick={() => setFilter("hoy")} title="Tareas con fecha compromiso para la jornada actual.">Vence hoy</FilterButton>
                            <FilterButton tone="info" count={summary.manana} active={filter === "manana"} onClick={() => setFilter("manana")} title="Tareas con fecha compromiso para el día siguiente.">Vence mañana</FilterButton>
                            <FilterButton tone="neutral" count={summary.sinFecha} active={filter === "sin_fecha"} onClick={() => setFilter("sin_fecha")} title="Tareas sin fecha compromiso registrada.">Sin fecha</FilterButton>
                            <FilterButton tone="success" count={summary.terminadas} active={filter === "terminada"} onClick={() => setFilter("terminada")} title="Tareas cerradas disponibles para consulta y trazabilidad.">Terminadas</FilterButton>
                        </div>
                    </div>
                    <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                        <ViewModeToggle value={viewMode} onChange={setViewMode} />
                        <FilterButton active={sortBy === "smart"} onClick={() => { setSortBy("smart"); setSortDir("asc"); }} title="Ordena por prioridad operativa: revisión, corrección, vencimiento, prioridad y fecha.">Orden inteligente</FilterButton>
                    </div>
                </div>
            </div>

            {loading ? (
                <div style={emptyBoxStyle}>Cargando tablero operativo...</div>
            ) : filteredTasks.length === 0 ? (
                <div style={emptyBoxStyle}>No hay tareas para este filtro. Ajusta la búsqueda o selecciona otra métrica del tablero.</div>
            ) : filter === "todas" ? (
                <>
                    <CollapsibleSection
                        title="Atención inmediata"
                        subtitle="Tareas vencidas, bloqueadas, en revisión o con corrección requerida. Son el mismo universo del tablero, solo priorizado por urgencia."
                        count={attentionImmediateTasks.length}
                        storageKey="mywork_attention_immediate"
                        defaultOpen={true}
                    >
                        {renderTaskCollection(attentionImmediateTasks)}
                    </CollapsibleSection>

                    <CollapsibleSection
                        title="Próximas acciones"
                        subtitle="Tareas activas sin señal crítica dominante, listas para avance, seguimiento o consulta operativa."
                        count={upcomingActionTasks.length}
                        storageKey="mywork_upcoming_actions"
                        defaultOpen={true}
                    >
                        {renderTaskCollection(upcomingActionTasks)}
                    </CollapsibleSection>

                    {consultationTasks.length ? (
                        <CollapsibleSection
                            title="Completadas y consulta"
                            subtitle="Tareas cerradas o canceladas disponibles para consulta histórica y trazabilidad."
                            count={consultationTasks.length}
                            storageKey="mywork_consultation_tasks"
                            defaultOpen={false}
                        >
                            {renderTaskCollection(consultationTasks)}
                        </CollapsibleSection>
                    ) : null}
                </>
            ) : (
                <CollapsibleSection title={filteredSectionTitle} subtitle={filteredSectionSubtitle} count={filteredTasks.length} storageKey={`mywork_filter_${filter}`} defaultOpen={true}>
                    {renderTaskCollection(orderedTasks)}
                </CollapsibleSection>
            )}
        </div>
    );
}
